import type { TranslationShape } from './en';

/**
 * Urdu translation.
 *
 * Typed against the English shape, so a key added to `en` and forgotten here is
 * a compile error rather than an English string appearing mid-sentence in an
 * otherwise Urdu screen.
 *
 * Departmental terms (XEN, SDO, Zilladar, Patwari, Tawan, Abiana, RD, challan)
 * are kept in the form officers actually use, transliterated where that is the
 * spoken usage. Translating "Sub-Divisional Officer" into invented formal Urdu
 * would make the interface harder to read, not easier.
 */
export const ur: TranslationShape = {
  app: {
    title: 'واٹر تھیفٹ اینڈ کنٹرول مانیٹرنگ سسٹم',
    shortTitle: 'ای ـ تاوان',
    department: 'محکمہ آبپاشی',
    government: 'حکومتِ پنجاب',
    tagline: 'پانی بچائیں، مستقبل محفوظ بنائیں',
  },

  theme: {
    command: 'کمانڈ',
    field: 'فیلڈ',
    switchLabel: 'ڈسپلے تھیم تبدیل کریں',
  },

  language: {
    switchTo: 'English',
  },

  nav: {
    primary: 'مرکزی مینو',
    dashboard: 'ڈیش بورڈ',
    wires: 'وائر',
    cases: 'تاوان کیسز',
    recovery: 'ریکوری',
    appeals: 'اپیلیں',
    publicReports: 'عوامی اطلاعات',
    handovers: 'حوالگی',
    audit: 'آڈٹ لاگ',
    admin: 'انتظامیہ',
  },

  role: {
    secretary: 'سیکرٹری آبپاشی',
    chief_engineer: 'چیف انجینئر',
    superintendent_engineer: 'سپرنٹنڈنگ انجینئر',
    xen: 'ایگزیکٹو انجینئر (XEN)',
    sdo: 'سب ڈویژنل آفیسر (SDO)',
    sub_engineer: 'سب انجینئر',
    zilladar: 'زلعدار',
    patwari: 'کینال پٹواری',
    data_operator: 'ڈیٹا آپریٹر',
    admin: 'سسٹم ایڈمنسٹریٹر',
    public_liaison: 'عوامی رابطہ سیل',
  },

  auth: {
    signInTitle: 'افسر سائن اِن',
    signIn: 'سائن اِن',
    signInWithGoogle: 'گوگل کے ساتھ جاری رکھیں',
    googleIntro:
      'اُسی گوگل اکاؤنٹ سے سائن اِن کریں جو آپ کے ایڈمنسٹریٹر نے درج کیا ہے۔ اس نظام کا کوئی الگ پاس ورڈ نہیں ہے۔',
    signingIn: 'سائن اِن ہو رہا ہے…',
    signOut: 'سائن آؤٹ',
    signOutAndRetry: 'سائن آؤٹ کر کے دوسرا اکاؤنٹ استعمال کریں',
    checking: 'آپ کا سیشن جانچا جا رہا ہے…',
    notAuthorisedTitle: 'سائن اِن ہو گیا، مگر ابھی رسائی نہیں',
    notAuthorisedBody:
      'یہ گوگل اکاؤنٹ ابھی ای-تاوان کے لیے مجاز نہیں۔ اگر ایڈمنسٹریٹر نے ابھی آپ کو درج کیا ہے تو رسائی عموماً پندرہ منٹ میں آ جاتی ہے — یہ صفحہ کھلا رہنے دیں۔ اگر اس سے زیادہ دیر ہو چکی ہے، یا آپ نے غلطی سے ذاتی اکاؤنٹ استعمال کیا ہے، تو سائن آؤٹ کر کے وہی پتہ استعمال کریں جو ایڈمنسٹریٹر نے درج کیا تھا۔',
    accountsProvisionedNote:
      'رسائی سسٹم ایڈمنسٹریٹر ایک مخصوص گوگل پتے کو دیتا ہے۔ نہ کوئی سائن اپ ہے، نہ کوئی پاس ورڈ جو ری سیٹ کرنا ہو۔',
    error: {
      accountDisabled:
        'اس اکاؤنٹ کی رسائی واپس لے لی گئی ہے۔ اپنے ڈویژنل ایڈمنسٹریٹر سے رابطہ کریں۔',
      popupBlocked:
        'آپ کے براؤزر نے گوگل سائن اِن ونڈو روک دی۔ اس سائٹ کے لیے پاپ اپ کی اجازت دیں اور دوبارہ کوشش کریں۔',
      providerDisabled:
        'اس تنصیب میں گوگل سائن اِن فعال نہیں ہے۔ یہ کنفیگریشن کی خرابی ہے — سسٹم ایڈمنسٹریٹر کو اطلاع دیں۔',
      tooManyAttempts: 'بہت زیادہ کوششیں۔ چند منٹ انتظار کر کے دوبارہ کوشش کریں۔',
      network: 'سرور تک رسائی نہ ہو سکی۔ اپنا کنکشن دیکھ کر دوبارہ کوشش کریں۔',
      unknown: 'سائن اِن ناکام۔ براہِ کرم دوبارہ کوشش کریں۔',
    },
    unprovisioned: {
      title: 'اکاؤنٹ کو ابھی کوئی کردار نہیں دیا گیا',
      body: 'آپ کا سائن اِن کامیاب رہا، مگر اس اکاؤنٹ کو ابھی کوئی کردار یا دائرۂ اختیار نہیں دیا گیا۔ اپنے سسٹم ایڈمنسٹریٹر سے سیٹ اپ مکمل کرنے کو کہیں۔ تب تک آپ کوئی مقدمہ نہیں دیکھ سکیں گے۔',
    },
  },

  setup: {
    title: 'فائربیس کنفیگر نہیں ہے',
    body: 'اس بلڈ کے ساتھ کوئی فائربیس پراجیکٹ منسلک نہیں، اس لیے سائن اِن بند ہے۔ ‎.env.example کی نقل بنا کر فائربیس ویب کنفیگریشن درج کریں:',
  },

  phase: {
    dashboard:
      'ایگزیکٹو کمانڈ سینٹر مرحلہ ۶ میں بنے گا، جان بوجھ کر سب سے آخر میں: یہ اُس ڈیٹا کا منظر ہے جو وائر، کیسز اور ریکوری مکمل ہونے سے پہلے وجود ہی نہیں رکھتا۔ پہلے بنانے سے صرف ایک خوبصورت اسکرین بنے گی جو فرضی اعداد سے بھری ہو گی۔',
    wires:
      'مرحلہ ۳ میں ڈیجیٹل وائر بنے گا: جی پی ایس، نہر/آر ڈی/کنارہ، موگھہ، متوقع ڈسچارج، اور افسر کی تحریری گواہی جو موقع کی تصاویر کی جگہ لے گی۔ وائر ایس ڈی او کو جائے گا، نقل خود بخود ایگزیکٹو انجینئر اور زلعدار کو۔',
    cases:
      'مرحلہ ۴ میں پٹواری (یا اُس کی طرف سے ڈیٹا آپریٹر، دونوں کا اندراج ہو گا) پرچہ تاوان بنائے گا، زلعدار تصدیق کرے گا، سماعت اور فیصلہ ہو گا — جرمانہ اُس نوٹیفکیشن کے حساب سے جو چوری پکڑے جانے کی تاریخ کو نافذ تھا۔',
    recovery:
      'مرحلہ ۵ میں چالان ۳۲-اے کا اندراج، اقساط کے واؤچر، دو افسران کی تصدیق، اور ٹریژری ریکنسیلی ایشن بنے گی — یہ واؤچر کی تصویر ختم کرنے کے بدلے میں رکھا گیا متبادل کنٹرول ہے۔ بقایا جات ۳۰/۶۰/۹۰+ دن کے حساب سے دکھائے جائیں گے۔',
    appeals:
      'مرحلہ ۵ میں اپیل کا نظام اور عدالتی حکمِ امتناعی کا ٹریکر بنے گا۔ زیرِ اپیل یا حکمِ امتناعی والا کیس الگ دکھایا جائے گا اور انتظامی التوا میں شمار نہیں ہو گا۔',
    publicReports:
      'مرحلہ ۲ میں عوامی اطلاع کا پورٹل اور افسران کا اِن باکس بنے گا۔ اطلاع میں صرف مقام کے کوآرڈینیٹس اور عوامی ویڈیو لنک ہوگا — اطلاع دینے والے کی کوئی شناخت نہیں۔',
    audit:
      'مرحلہ ۷ میں آڈٹ لاگ دیکھنے کی سہولت بنے گی۔ اندراجات صرف شامل ہوتے ہیں: کوئی بھی، بشمول ایڈمنسٹریٹر، نہ تبدیل کر سکتا ہے نہ حذف۔',
    admin:
      'مرحلہ ۷ میں صارفین کا انتظام بنے گا: بلک CSV امپورٹ (پہلے آزمائشی جائزہ، سطر بہ سطر غلطیاں، واپسی کی سہولت)، روٹنگ رولز، ماسٹر ڈیٹا اور نرخ نوٹیفکیشن کی تصدیق۔',
  },

  placeholder: {
    yourCapabilities: 'آپ کا عہدہ کیا کر سکتا ہے',
  },

  public: {
    nav: 'عوامی مینو',
    home: 'صفحۂ اول',
    report: 'چوری کی اطلاع دیں',
    track: 'اطلاع کی صورتحال',
    officerSignIn: 'افسر سائن اِن',
  },

  platform: {
    youtube: 'یوٹیوب',
    facebook: 'فیس بک',
    x: 'ایکس',
    instagram: 'انسٹاگرام',
    tiktok: 'ٹک ٹاک',
  },

  landing: {
    heading: 'پنجاب کی نہر پر پانی کی چوری کی اطلاع دیں',
    intro:
      'اوپر سے ناجائز لیا گیا پانی وہ ہے جو دم پر بیٹھے کاشتکار تک کبھی نہیں پہنچتا۔ اگر آپ نے نہر کے کنارے میں کٹ، ناجائز پائپ، یا بغیر اجازت پانی اٹھاتا پمپ دیکھا ہے تو یہاں اطلاع دے سکتے ہیں۔',
    cta: 'پانی کی چوری کی اطلاع دیں',

    privacy: {
      heading: 'آپ کی اطلاع کا علم کس کو ہوگا',
      intro:
        'یہ فارم آپ کا نام، والد کا نام، شناختی کارڈ نمبر، موبائل نمبر اور پتہ مانگتا ہے، کیونکہ جس اطلاع کی تصدیق نہ ہو سکے اس پر محکمہ کارروائی نہیں کر سکتا۔ یہ تفصیلات عوامی رابطہ سیل کے پاس رہتی ہیں، جو آپ کو فون کر کے تصدیق کرتا ہے، اور سیکرٹری کے پاس۔ نہر پر جانے والے افسران تک یہ نہیں جاتیں۔',
      weSeeHeading: 'عوامی رابطہ سیل کو کیا ملتا ہے',
      weSee1: 'آپ کا نام، والد کا نام، شناختی کارڈ نمبر، موبائل نمبر اور پتہ',
      weSee2: 'اس ویڈیو کا لنک جس کی طرف آپ اشارہ کرتے ہیں',
      weSee3: 'مقام، اور جو کچھ آپ نوٹ میں لکھیں',
      weDontHeading: 'فیلڈ افسران کو کیا ملتا ہے',
      weDont1: 'ٹکٹ نمبر اور مقام — آپ کا نام یا شناختی کارڈ نمبر کبھی نہیں',
      weDont2:
        'ویڈیو کا لنک بھی نہیں: نہر کے کنارے کی فلم سے یہ ظاہر ہو سکتا ہے کہ وہاں کھڑا ہو کر کون فلما رہا تھا',
      weDont3: 'چوری کی تحریری تفصیل، جو سیل لکھتا ہے، آپ نہیں',
      footnote:
        'ہم اب بھی آپ کا آئی پی ایڈریس یا آپ کے آلے کے بارے میں کچھ ریکارڈ نہیں کرتے، اور آپ کا ٹریکنگ کوڈ صرف یک طرفہ ہیش کی صورت میں محفوظ ہوتا ہے۔ پورے نظام میں صرف دو کردار دیکھ سکتے ہیں کہ اطلاع کس نے دی، اور سیکورٹی قواعد باقی سب کو انکار کرتے ہیں — بشمول اُس افسر کے جو تفتیش کرتا ہے۔',
    },

    impact: {
      heading: 'یہ کیوں اہم ہے',
      body: 'نہر سانجھی ہے۔ سر پر بغیر اجازت موڑا گیا پانی غائب نہیں ہوتا — وہ دم پر بیٹھے اُن کاشتکاروں سے لیا جاتا ہے جن کے پاس شکایت کرنے کی سب سے کم گنجائش ہوتی ہے۔ ناجائز استعمال کناروں اور موگھوں کو بھی نقصان پہنچاتا ہے، جو پھر سب کے لیے ناکارہ ہو جاتے ہیں۔',
    },

    law: {
      heading: 'قانونی حیثیت',
      body: 'نہری پانی کا ناجائز استعمال نہر و نکاس ایکٹ کے تحت جرم ہے۔ اس پر خصوصی تاوان، ناجائز کنکشن کی بندش، اور سنگین صورتوں میں پولیس مقدمہ ہو سکتا ہے۔ جرمانہ سماعت کے بعد ایس ڈی او مقرر کرتا ہے۔',
    },

    how: {
      heading: 'اطلاع پر کارروائی کیسے ہوتی ہے',
      step1Title: 'آپ اطلاع دیتے ہیں',
      step1Body:
        'آپ اپنی تفصیلات، مقام، اور اُس ویڈیو کا لنک دیتے ہیں جو آپ نے دیکھا۔ آپ کو ٹریکنگ کوڈ ملتا ہے۔',
      step2Title: 'عوامی رابطہ سیل آپ کو فون کرتا ہے',
      step2Body:
        'سیل کا رکن فون کر کے تصدیق کرتا ہے کہ آپ کون ہیں اور آپ نے کیا دیکھا، ویڈیو دیکھتا ہے، اور اس کی تفصیل لکھتا ہے۔ اس کے بعد وہی تفصیل آگے جاتی ہے — آپ کی تفصیلات سیل کے پاس رہتی ہیں۔',
      step3Title: 'زون قبول کرتا ہے، افسر تفتیش کرتا ہے',
      step3Body:
        'سیل متعلقہ زون کے چیف انجینئر کو ٹکٹ بھیجتا ہے، جو اسے کسی سب ڈویژن کے سپرد کرتا ہے۔ اگر موقع پر چوری ثابت ہو جائے تو باقاعدہ مقدمہ بنتا ہے۔ دونوں صورتوں میں آپ اپنے کوڈ سے حالت دیکھ سکتے ہیں۔',
    },

    safety: {
      heading: 'براہِ کرم محتاط رہیں',
      body: 'کسی سے تصادم نہ کریں اور ویڈیو بنانے کے لیے خود کو خطرے میں نہ ڈالیں۔ اگر صورتحال خطرناک لگے تو قریب نہ جائیں — جو محفوظ طریقے سے دیکھ سکتے ہیں وہی بتائیں، یا پولیس سے رابطہ کریں۔ نفاذ محکمے کا کام ہے، بوقتِ ضرورت پولیس اور پیرا کے ساتھ۔ یہ آپ کا کام نہیں۔ جان بوجھ کر جھوٹی اطلاع دینا خود جرم ہے، اور محکمہ اسے پولیس کو بھیج سکتا ہے۔',
    },
  },

  report: {
    heading: 'پانی چوری کی اطلاع',

    /* منتقلی کے عنوانات، جو قطار میں بٹن بھی بنتے ہیں۔ */
    verifyIdentity: 'اطلاع دہندہ کی تصدیق ہو گئی',
    dismiss: 'آگے بھیجے بغیر بند کریں',
    forward: 'زون کو بھیجیں',
    forwardAgain: 'دوبارہ بھیجیں',
    accept: 'اس زون میں قبول کریں',
    returnToCell: 'سیل کو واپس کریں',
    assignToSdo: 'سب ڈویژن کے سپرد کریں',
    reassign: 'دوبارہ سپرد کریں',
    beginInvestigation: 'تفتیش شروع کریں',
    wrongJurisdiction: 'یہ سب ڈویژن نہیں',
    convertToWire: 'چوری ثابت — مقدمہ بنائیں',
    closeNoTheft: 'بند کریں — کچھ نہ ملا',
    markFake: 'جھوٹی قرار دیں',

    intro:
      'تین چیزیں درکار ہیں: آپ کون ہیں، یہ کہاں ہو رہا ہے، اور اُس کی ویڈیو کا لنک۔ ویڈیو کے لنک کے بغیر اطلاع بھیجی نہیں جا سکتی۔',

    confidentialityTitle: 'آپ کی تفصیلات کہاں جاتی ہیں',
    confidentialityBody:
      'آپ کا نام، شناختی کارڈ نمبر اور موبائل نمبر عوامی رابطہ سیل دیکھتا ہے، جو آپ کو فون کر کے تصدیق کرے گا، اور سیکرٹری۔ یہ چیف انجینئر یا موقع پر جانے والے افسر کو نہیں دی جاتیں — انہیں ٹکٹ نمبر، مقام، اور سیل کی لکھی ہوئی تفصیل ملتی ہے۔ ویڈیو کا لنک بھی سیل کے پاس رہتا ہے، کیونکہ نہر کے کنارے کی فلم سے یہ ظاہر ہو سکتا ہے کہ وہاں کھڑا ہو کر کون فلما رہا تھا۔',

    reporterLegend: 'اطلاع کون دے رہا ہے',
    reporterHelp:
      'ٹکٹ آگے بھیجنے سے پہلے سیل ہر اطلاع دہندہ کو فون کرتا ہے۔ جس اطلاع کی تصدیق نہ ہو سکے اس پر کارروائی نہیں ہو سکتی، اسی لیے یہ خانے لازمی ہیں، اختیاری نہیں۔',
    reporterName: 'آپ کا نام',
    reporterFatherName: 'والد کا نام',
    reporterCnic: 'شناختی کارڈ نمبر',
    reporterPhone: 'موبائل نمبر',
    reporterAddress: 'پتہ',
    phoneHelp: 'ایسا نمبر دیں جس پر آپ جواب دیں۔ سیل تصدیق کے لیے اسی نمبر پر کال کرے گا۔',

    locationLegend: 'یہ کہاں ہو رہا ہے؟',
    locationHelp:
      'اگر آپ موقع پر ہیں تو اپنا موجودہ مقام استعمال کریں، ورنہ کوآرڈینیٹس لکھ دیں۔ اس صفحے پر نقشہ جان بوجھ کر نہیں رکھا گیا — نقشے کی ٹائلیں کسی اور کمپنی کو بتا دیتیں کہ آپ کہاں ہیں۔',
    useMyLocation: 'میرا موجودہ مقام استعمال کریں',
    locating: 'مقام معلوم کیا جا رہا ہے…',
    gpsDenied:
      'مقام تک رسائی سے انکار ہو گیا۔ آپ نیچے کوآرڈینیٹس خود لکھ سکتے ہیں، یا اجازت دے کر دوبارہ کوشش کریں۔',
    gpsUnavailable: 'یہ آلہ مقام فراہم نہیں کر سکتا۔ براہِ کرم نیچے کوآرڈینیٹس لکھیں۔',
    latitude: 'عرض بلد (Latitude)',
    longitude: 'طول بلد (Longitude)',
    locationHint: 'نہر اور آر ڈی، اگر معلوم ہو (اختیاری)',
    locationHintPlaceholder: 'بی آر بی کینال، آر ڈی ۱۴۲+۵۰۰ کے قریب، بایاں کنارہ',
    locationHintHelp:
      'آبپاشی افسران نہر کے نام، آر ڈی اور کنارے سے جگہ پہچانتے ہیں۔ موٹی موٹی تفصیل بھی مدد دیتی ہے۔',

    videoLegend: 'ویڈیو شہادت',
    videoHelp:
      'وہ ویڈیو کا لنک لگائیں جو آپ پہلے ہی یوٹیوب، فیس بک، ایکس، انسٹاگرام یا ٹک ٹاک پر شائع کر چکے ہیں۔ یہاں کوئی فائل اپلوڈ نہ کریں — یہ نظام کوئی فائل محفوظ نہیں کرتا۔',
    videoUrl: 'ویڈیو کا لنک',
    videoRecognised: '{{platform}} کا لنک پہچان لیا گیا۔',

    notesLegend: 'آپ نے کیا دیکھا؟ (اختیاری)',
    notesHelp: 'چند سطریں کافی ہیں۔ یہ کب اور کیسے ہوتا ہے، یہ سب سے کارآمد بات ہے۔',
    notes: 'تفصیل',

    submit: 'اطلاع بھیجیں',
    submitting: 'بھیجی جا رہی ہے…',
    privacyReminder:
      'آپ کی تفصیلات عوامی رابطہ سیل اور سیکرٹری تک جاتی ہیں۔ تفتیش کرنے والے افسران کو کبھی نہیں دی جاتیں۔ آئی پی ایڈریس یا آلے کی معلومات جمع نہیں کی جاتیں۔',
    turnstileStubbed:
      'اس ڈویلپمنٹ بلڈ میں بوٹ چیک عارضی طور پر بند ہے۔ اصل نظام میں ہمیشہ تصدیق ہوتی ہے۔',

    done: {
      heading: 'اطلاع موصول ہو گئی',
      body: 'آپ کی اطلاع عوامی رابطہ سیل تک پہنچ گئی ہے۔ آگے بھیجنے سے پہلے کوئی رکن آپ کے دیے گئے نمبر پر فون کر کے تصدیق کرے گا۔',
      codeLabel: 'آپ کا ٹریکنگ کوڈ',
      writeItDown:
        'یہ کوڈ ابھی لکھ لیں۔ یہ صرف ایک طرفہ ہیش کی صورت محفوظ ہے، اس لیے کوئی بھی — محکمہ بھی — اسے نہ دیکھ سکتا ہے نہ دوبارہ بھیج سکتا ہے۔',
      checkStatus: 'صورتحال دیکھیں',
      backHome: 'صفحۂ اول پر واپس',
    },

    error: {
      coordinatesOutsidePunjab:
        'یہ کوآرڈینیٹس پنجاب سے باہر ہیں۔ براہِ کرم عرض بلد اور طول بلد دیکھ لیں۔',
      nameRequired: 'براہِ کرم اپنا پورا نام لکھیں۔',
      fatherNameRequired: 'براہِ کرم والد کا نام لکھیں۔',
      cnicInvalid:
        'شناختی کارڈ نمبر درست نہیں لگتا۔ یہ ۱۳ ہندسوں کا ہوتا ہے، مثلاً 35202-1234567-1۔',
      phoneInvalid:
        'موبائل نمبر درست نہیں لگتا۔ 03001234567 کی صورت میں لکھیں — سیل اسی پر کال کرے گا۔',
      addressRequired: 'براہِ کرم ایسا پتہ لکھیں جس کا سیل حوالہ دے سکے۔',
      notVerifiedYet: 'آگے بھیجنے سے پہلے اطلاع دہندہ کو فون کریں اور کال ریکارڈ کریں۔',
      summaryTooShort:
        'لکھیں کہ ویڈیو میں کیا ہے۔ چیف انجینئر اور ایس ڈی او کو صرف یہی تفصیل ملے گی — ویڈیو ٹکٹ کے ساتھ نہیں جاتی۔',
      zoneRequired: 'وہ زون منتخب کریں جس سے یہ تعلق رکھتا ہے۔',
      subDivisionRequired: 'وہ سب ڈویژن منتخب کریں جس کے سپرد کرنا ہے۔',
      fakeReasonTooShort:
        'کسی اطلاع کو جھوٹی قرار دینا اطلاع دہندہ کے ساتھ اگلی کال تک جاتا ہے۔ تفصیل سے لکھیں کہ آپ کو کیا ملا۔',
      siteVisitRequired:
        'اپنے موقع کے دورے کی ریکارڈنگ کا حوالہ دیں۔ جھوٹی قرار دینے کے لیے دورہ ریکارڈ پر ہونا ضروری ہے۔',
      videoRequired: 'ویڈیو کا لنک ضروری ہے۔',
      videoNotRecognised:
        'یہ لنک پہچانا نہیں گیا۔ یہ یوٹیوب، فیس بک، ایکس، انسٹاگرام یا ٹک ٹاک کی عوامی ویڈیو ہونی چاہیے۔',
      notesTooLong: 'تفصیل بہت طویل ہے۔',
      locationHintTooLong: 'مقام کی تفصیل بہت طویل ہے۔',
      turnstileMissing: 'بوٹ چیک مکمل ہونے کا انتظار کریں، پھر دوبارہ بھیجیں۔',
      turnstileFailed: 'بوٹ چیک کامیاب نہیں ہوا۔ صفحہ دوبارہ لوڈ کر کے کوشش کریں۔',
      turnstileUnavailable: 'بوٹ چیک لوڈ نہیں ہو سکا۔ کنکشن دیکھ کر صفحہ دوبارہ لوڈ کریں۔',
      busyTryLater:
        'نظام اس وقت مصروف ہے اور آپ کی اطلاع قبول نہیں کر سکا۔ تھوڑی دیر بعد دوبارہ کوشش کریں — کچھ ضائع نہیں ہوا۔',
      malformed: 'اطلاع پڑھی نہیں جا سکی۔ صفحہ دوبارہ لوڈ کر کے کوشش کریں۔',
      serverError: 'ہماری طرف سے کوئی مسئلہ ہو گیا۔ دوبارہ کوشش کریں۔',
      network: 'سرور تک رسائی نہیں ہو سکی۔ کنکشن دیکھ کر دوبارہ کوشش کریں۔',
      notConfigured: 'اس تنصیب پر اطلاع دینے کا نظام ابھی مرتب نہیں ہوا۔',
    },
  },

  track: {
    heading: 'اطلاع کی صورتحال',
    intro: 'وہ ٹریکنگ کوڈ درج کریں جو اطلاع دیتے وقت آپ کو ملا تھا۔',
    codeLabel: 'ٹریکنگ کوڈ',
    search: 'صورتحال دیکھیں',
    searching: 'دیکھا جا رہا ہے…',
    statusLabel: 'موجودہ صورتحال',
    submittedOn: '{{date}} کو درج ہوئی',
    noOfficerDetails:
      'صرف پیش رفت دکھائی جاتی ہے۔ کون سا افسر اسے دیکھ رہا ہے، یہ جان بوجھ کر ظاہر نہیں کیا جاتا۔',
    notFound:
      'اس کوڈ سے کوئی اطلاع نہیں ملی۔ لکھنے میں غلطی دیکھ لیں — کوڈ میں انگریزی O اور صفر شامل نہیں ہوتے۔',
    serverError: 'اس وقت صورتحال نہیں دیکھی جا سکی۔ تھوڑی دیر بعد کوشش کریں۔',
  },

  reportState: {
    SUBMITTED: 'موصول — سیل کے انتظار میں',
    IDENTITY_VERIFIED: 'اطلاع دہندہ کی فون پر تصدیق ہو گئی',
    FORWARDED: 'زون کو بھیج دی گئی',
    ACCEPTED: 'زون نے قبول کر لی',
    WITH_SDO: 'ایس ڈی او کے پاس',
    UNDER_INVESTIGATION: 'موقع پر تفتیش جاری',
    CONVERTED_TO_WIRE: 'چوری ثابت — مقدمہ درج',
    CLOSED_NO_THEFT: 'بند — معائنے پر کچھ نہ ملا',
    MARKED_FAKE: 'بند — جھوٹی پائی گئی',
    RETURNED_TO_CELL: 'سیل کو واپس',
    RETURNED_TO_ZONE: 'زون کو واپس — غلط حلقہ',
    DISMISSED_BY_CELL: 'سیل نے بند کر دی',
  },

  inbox: {
    loading: 'اطلاعات لوڈ ہو رہی ہیں…',
    error: 'اطلاعات لوڈ نہیں ہو سکیں۔',
    count: '{{count}} اطلاعات',
    empty: 'آپ کے لیے کوئی عوامی اطلاع زیرِ التوا نہیں۔',
    cellIntro:
      'ہر اطلاع پہلے یہاں آتی ہے۔ اطلاع دہندہ کو فون کریں، ویڈیو دیکھیں، لکھیں کہ اس میں کیا ہے، اور زون کو ٹکٹ بھیجیں۔ آپ جو لکھیں گے، آگے والے افسران کے پاس صرف وہی تفصیل ہوگی۔',
    fieldIntro:
      'یہ ٹکٹ عوامی رابطہ سیل نے بھیجے ہیں۔ اطلاع دہندہ کا نام اور ویڈیو سیل کے پاس رہتے ہیں — نیچے دی گئی تفصیل سیل کا اپنا بیان ہے کہ ویڈیو میں کیا تھا۔',
    openTicket: 'کھولیں',
    collapse: 'بند کریں',
    nothingToDo: 'اس مرحلے پر اس ٹکٹ پر آپ کے کرنے کا کچھ نہیں۔',
    actions: 'آپ کیا کر سکتے ہیں',
    reason: 'وجہ (آڈٹ لاگ میں درج ہوگی)',

    corroborated: '{{count}} بار اطلاع دی گئی',
    cellSummary: 'سیل نے کیا درج کیا',
    noSummaryYet: 'سیل نے ابھی تفصیل نہیں لکھی۔',
    coordinates: 'کوآرڈینیٹس',
    locationHint: 'بتایا گیا مقام',
    noHint: 'نہیں بتایا گیا',
    citizenNotes: 'شہری نے کیا لکھا',

    reporter: 'اطلاع دہندہ',
    reporterConfidential:
      'صرف عوامی رابطہ سیل اور سیکرٹری کو نظر آتا ہے۔ جو ٹکٹ زون یا سب ڈویژن کو جاتا ہے، اس پر یہ نہیں ہوتا۔',
    reporterMissing:
      'اس ٹکٹ کا کوئی اطلاع دہندہ ریکارڈ نہیں۔ ایسا نہیں ہونا چاہیے — ٹکٹ پر کارروائی کرنے کے بجائے ایڈمنسٹریٹر کو بتائیں۔',
    reporterName: 'نام',
    reporterFather: 'والد کا نام',
    reporterCnic: 'شناختی کارڈ نمبر',
    reporterPhone: 'موبائل',
    reporterAddress: 'پتہ',
    reporterHistory: 'پچھلی اطلاعات',
    historyLine: '{{total}} دی گئیں · {{confirmed}} ثابت ہوئیں · {{fake}} جھوٹی نکلیں',
    historyWarning:
      'احتیاط سے پیش آئیں: اس شخص کی {{total}} اطلاعات میں سے {{fake}} موقع کے دورے کے بعد جھوٹی نکلیں۔',
    warningSuggested:
      'کال پر انہیں بتائیں کہ مزید جھوٹی اطلاع ایف آئی اے یا مقامی تھانے کو بھیج دی جائے گی، پھر درج کریں کہ آپ نے بتا دیا۔',
    alreadyWarned: 'اس اطلاع دہندہ کو یہ تنبیہ پہلے دی جا چکی ہے۔',
    recordWarning: 'درج کریں کہ تنبیہ دی گئی',

    callNote: 'کال پر اطلاع دہندہ نے کیا تصدیق کی',
    recordCall: 'کال درج کریں',
    verified: 'فون پر تصدیق ہو گئی۔ {{note}}',

    summaryLabel: 'ویڈیو میں کیا ہے',
    summaryPlaceholder:
      'مثلاً: پُل سے تقریباً ۲۰۰ فٹ نیچے بائیں کنارے میں ۴ انچ کا پائپ، رات کو چلتا ہے؛ ۱۲ ستمبر کی فلم۔',
    summaryHelp:
      'کم از کم {{n}} حروف۔ چیف انجینئر اور ایس ڈی او کو صرف یہی ملتا ہے — نہ ویڈیو ٹکٹ کے ساتھ جاتی ہے، نہ شہری کے اپنے الفاظ۔',
    zone: 'زون',
    chooseZone: 'زون منتخب کریں…',
    noZones: 'ابھی کوئی زون درج نہیں ہوا۔ ٹکٹ بھیجنے سے پہلے ایڈمنسٹریٹر کو زون شامل کرنے ہوں گے۔',
    noSubDivisions:
      'اس زون کے لیے ابھی کوئی سب ڈویژن درج نہیں ہوا۔ ٹکٹ سپرد کرنے سے پہلے ایڈمنسٹریٹر کو یہ شامل کرنے ہوں گے۔',
    orgUnreadable: 'تنظیمی ڈھانچہ پڑھا نہیں جا سکا: {{message}}',
    subDivision: 'سب ڈویژن',
    chooseSubDivision: 'سب ڈویژن منتخب کریں…',
    siteVisitReference: 'موقع کے دورے کی ریکارڈنگ کا حوالہ',
    siteVisitHelp:
      'آپ کے دورے کی ریکارڈنگ کہاں ملے گی — آپ کا چینل اور ویڈیو کا عنوان، یا اس کا لنک۔ جھوٹی قرار دینا اطلاع دہندہ کے ساتھ جاتا ہے، اس لیے دورہ ریکارڈ پر ہونا ضروری ہے۔',

    showVideo: 'ویڈیو دکھائیں',
    hideVideo: 'ویڈیو چھپائیں',
    openOnPlatform: '{{platform}} پر کھولیں',
    videoTitle: 'شہری کی بھیجی ہوئی ویڈیو شہادت',
    videoMayBeDeleted:
      'یہ ویڈیو پلیٹ فارم پر ہے اور پوسٹ کرنے والا اسے حذف کر سکتا ہے۔ جو کچھ اس میں ہے، اسے ابھی اپنی تفصیل میں لکھ لیں۔',
  },

  common: {
    back: 'واپس',
    confirm: 'تصدیق کریں',
    save: 'محفوظ کریں',
    cancel: 'منسوخ کریں',
    loading: 'لوڈ ہو رہا ہے…',
    signInRequired: 'جاری رکھنے کے لیے سائن اِن کریں۔',
  },

  unit: {
    cusecs: 'کیوسک',
    acreFeet: 'ایکڑ فٹ',
  },

  theftType: {
    cut_in_bank: 'کنارے میں کٹ',
    pipe_installation: 'غیر قانونی پائپ',
    crest_tampering: 'موگھے کی کرسٹ سے چھیڑ چھاڑ',
    diesel_pump: 'ڈیزل پمپ سے پانی اٹھانا',
    siphon: 'کنارے کے اوپر سے سائفن',
    gate_tampering: 'گیٹ سے چھیڑ چھاڑ',
    unauthorised_outlet: 'غیر منظور شدہ موگھہ',
    other: 'دیگر',
  },

  wireState: {
    DRAFT: 'مسودہ',
    DISPATCHED: 'ایس ڈی او کے پاس',
    SDO_CONFIRMED: 'ایس ڈی او سے تصدیق شدہ',
    REJECTED: 'مسترد',
    CONVERTED_TO_TAWAN: 'تاوان کیس بن گیا',
  },

  escalation: {
    none: 'مقررہ مدت کے اندر ہے۔',
    nextIn: 'کارروائی نہ ہوئی تو تقریباً {{hours}} گھنٹے میں {{level}} تک جائے گا۔',
    NONE: '',
    AMBER_XEN: 'ایگزیکٹو انجینئر کو اطلاع — ۴۸ گھنٹے گزر چکے ہیں۔',
    AMBER_LOGGED: '۷۲ گھنٹے بعد بھی کارروائی نہیں۔ اس سب ڈویژن کے کھاتے میں درج۔',
    RED_CE_SECRETARY: 'چیف انجینئر اور سیکرٹری کو اطلاع — ۷ دن سے زائد کارروائی نہیں ہوئی۔',

    label: {
      NONE: '',
      AMBER_XEN: 'ایگزیکٹو انجینئر',
      AMBER_LOGGED: 'ڈویژنل ریکارڈ',
      RED_CE_SECRETARY: 'چیف انجینئر اور سیکرٹری',
    },
  },

  wire: {
    raiseNew: 'نیا وائر',
    newHeading: 'چوری کا وائر بھیجیں',
    loading: 'وائر لوڈ ہو رہے ہیں…',
    loadError: 'وائر لوڈ نہیں ہو سکے۔',
    empty: 'ابھی کوئی وائر نہیں۔',
    justNow: 'ابھی',

    locationLegend: 'چوری کہاں ہو رہی ہے؟',
    locationHelp:
      'اگر آپ موقع پر ہیں تو اپنی پوزیشن محفوظ کریں۔ نہر، آر ڈی اور کنارہ وہی ہیں جن سے ایس ڈی او جگہ ڈھونڈے گا — جی پی ایس نہ ہو تب بھی یہ ضرور لکھیں۔',
    useMyLocation: 'میری پوزیشن لیں',
    locating: 'پوزیشن لی جا رہی ہے…',
    gpsDenied: 'مقام تک رسائی سے انکار ہو گیا۔ نیچے نہر، آر ڈی اور کنارہ کافی ہیں۔',
    gpsUnavailable: 'یہ آلہ مقام فراہم نہیں کر سکتا۔ نیچے نہر، آر ڈی اور کنارہ درج کریں۔',
    canal: 'نہر',
    selectCanal: 'نہر منتخب کریں…',
    rd: 'آر ڈی',
    rdHelp: 'ہیڈ ورکس سے فاصلہ، مثلاً ۱۴۲+۵۰۰۔',
    bank: 'کنارہ',
    bankLeft: 'بایاں کنارہ (L/B)',
    bankRight: 'دایاں کنارہ (R/B)',
    mogha: 'موگھہ نمبر (اختیاری)',
    moghaPlaceholder: 'مثلاً 142-R',
    moghaHelp: 'جب چوری موگھے پر ہو، دو موگھوں کے درمیان نہ ہو، تو یہ درج کریں۔',
    coordinates: 'کوآرڈینیٹس',
    noCoordinates: 'محفوظ نہیں ہوئے',

    theftLegend: 'کیا ہو رہا ہے؟',
    theftType: 'طریقہ',
    discharge: 'اندازاً ڈسچارج',
    dischargeHelp:
      'کیوسک میں۔ ایک غیر قانونی پائپ عموماً ۵ سے کافی کم ہوتا ہے۔ زیادہ سے زیادہ {{max}}۔',

    attestationLegend: 'آپ نے کیا دیکھا؟',
    attestationHelp:
      'یہ نظام کوئی تصویر محفوظ نہیں کرتا، اس لیے آپ کی تحریر ہی شہادت ہے۔ لکھیں کہ آپ نے کیا پایا، بالکل کہاں، اور کیا کارروائی کی — یہی سماعت میں پڑھا جائے گا اور اپیل میں دیکھا جائے گا۔',
    narrative: 'آپ کا بیان',
    narrativeMore: 'مزید {{n}} حروف درکار ہیں۔',
    narrativeOk: 'کارروائی کے لیے کافی ہے۔',
    attestation: 'افسر کا بیان',

    dispatchToSdo: 'ایس ڈی او کو بھیجیں',
    saveDraft: 'مسودہ محفوظ کریں',
    offlineNote:
      'یہ سگنل کے بغیر بھی کام کرتا ہے۔ وائر اس آلے میں محفوظ رہتا ہے اور کنکشن آتے ہی خود بخود بھیج دیا جاتا ہے۔',

    pendingSync: '{{count}} وائر ابھی اسی آلے میں ہیں، سرور تک نہیں پہنچے۔',
    offlineShowingCached: 'آپ آف لائن ہیں۔ جو اس آلے میں محفوظ ہے وہی دکھایا جا رہا ہے۔',
    notYetSynced: 'ابھی بھیجا نہیں گیا',

    confirm: 'چوری کی تصدیق کریں',
    reject: 'مسترد کریں',
    rejectReason: 'مسترد کرنے کی وجہ؟ (مستقل درج ہو گی)',
    confirmReject: 'وائر مسترد کریں',
    rejectedBecause: 'مسترد: {{reason}}',
    cannotJudgeOwn: 'یہ وائر آپ نے بھیجا تھا، اس لیے تصدیق کوئی دوسرا افسر کرے گا۔',

    error: {
      invalid: 'براہِ کرم نشان زدہ خانہ دیکھیں۔',
      canalRequired: 'نہر منتخب کریں۔',
      rdInvalid: 'آر ڈی عدد میں لکھیں، یا آر ڈی+فٹ کی صورت میں — مثلاً 142+500۔',
      bankRequired: 'کنارہ منتخب کریں۔',
      theftTypeRequired: 'منتخب کریں کہ پانی کس طرح لیا جا رہا ہے۔',
      dischargeRequired: 'اندازاً ڈسچارج کیوسک میں درج کریں۔',
      dischargeTooSmall: 'یہ ڈسچارج بہت کم لگتا ہے۔ عدد کیوسک میں لکھیں۔',
      dischargeTooLarge: 'یہ ایک چوری کے لیے معمول سے بہت زیادہ ہے — اعشاریہ دیکھ لیں۔',
      narrativeTooShort:
        'جو آپ نے دیکھا اُسے اتنی تفصیل سے لکھیں کہ سماعت میں کارروائی ہو سکے۔ چونکہ کوئی تصویر محفوظ نہیں ہوتی، آپ کی تحریر ہی شہادت ہے۔',
      saveFailed: 'وائر محفوظ نہیں ہو سکا۔ دوبارہ کوشش کریں۔',
    },
  },

  season: {
    kharif: 'خریف',
    rabi: 'ربیع',
  },

  crop: {
    wheat: 'گندم',
    cotton: 'کپاس',
    sugarcane: 'گنا',
    rice: 'چاول',
    maize: 'مکئی',
    fodder: 'چارہ',
    orchard: 'باغ',
    vegetables: 'سبزیاں',
    other: 'دیگر',
  },

  caseState: {
    FRAMED: 'پرچہ بن گیا',
    UNDER_VERIFICATION: 'زلعدار کے پاس',
    VERIFIED: 'تصدیق شدہ — ایس ڈی او کے پاس',
    HEARING_SCHEDULED: 'سماعت مقرر',
    DECIDED: 'فیصلہ ہو گیا',
    RECOVERY_PENDING: 'ریکوری باقی',
    PARTIALLY_RECOVERED: 'جزوی ریکوری',
    FULLY_RECOVERED: 'مکمل ریکوری',
    ARREARS_REFERRED: 'بقایا جات میں منتقل',
    UNDER_APPEAL: 'زیرِ اپیل',
    STAYED: 'عدالتی حکمِ امتناعی',
    VOID: 'منسوخ',
  },

  outcome: {
    FINE_IMPOSED: 'جرمانہ عائد',
    FIR_REGISTERED: 'ایف آئی آر درج',
    CONNECTION_DISCONNECTED: 'کنکشن منقطع',
    METER_SEALED: 'موگھہ سیل',
    WATER_RELEASED: 'جرمانے کے بعد پانی بحال',
    WARNING: 'تنبیہ',
    EXONERATED: 'بری',
  },

  case: {
    submitForVerification: 'زلعدار کو بھیجیں',
    returnForCorrection: 'درستی کے لیے واپس',
    countersign: 'تصدیق کر کے ایس ڈی او کو بھیجیں',
    scheduleHearing: 'سماعت مقرر کریں',
    recordDecision: 'فیصلہ درج کریں',
    adjourn: 'ملتوی کریں',
    raiseDemand: 'مطالبہ جاری کریں',
    recordPartialRecovery: 'جزوی ریکوری درج کریں',
    recordFullRecovery: 'مکمل ریکوری درج کریں',
    referToTehsildar: 'بقایا جات میں منتقل کریں',
    recordArrearsRecovery: 'بقایا جات کی ریکوری درج کریں',
    recordAppealFiled: 'اپیل درج کریں',
    recordAppealDecision: 'اپیل کا فیصلہ درج کریں',
    remandForRehearing: 'دوبارہ سماعت کے لیے واپس',
    recordStayOrder: 'حکمِ امتناعی درج کریں',
    vacateStay: 'حکمِ امتناعی ختم',
    void: 'یہ کیس منسوخ کریں',
  },

  tawan: {
    season: 'کون سا موسم لگے گا',
    seasonChoose: 'موسم منتخب کریں…',
    multiplierLabel: 'جرمانہ، آبیانہ کے کتنے گنا',
    multiplierHelp:
      'سماعت میں آپ کا فیصلہ: ایک سے دس گنا تک، چوری کی سنگینی اور ملزم کے بیان کو دیکھتے ہوئے۔ نیچے کی رقم اسی سے نکلتی ہے — یہ ٹائپ نہیں کی جاتی۔',
    penaltyDecidedAtHearing:
      'سادہ آبیانہ ×1، صرف اندازے کے لیے۔ کون سا موسم لگے گا اور کتنے گنا — ایک سے دس تک — یہ ایس ڈی او سماعت میں طے کرتا ہے، اس لیے اصل مطالبہ ابھی معلوم نہیں۔',
    baseKharif: 'اگر خریف شمار ہو',
    baseRabi: 'اگر ربیع شمار ہو',
    count: '{{count}} کیسز',
    loading: 'کیسز لوڈ ہو رہے ہیں…',
    loadError: 'کیسز لوڈ نہیں ہو سکے۔',
    empty: 'ابھی کوئی کیس نہیں۔',
    awaitingNumber: 'نمبر باقی',
    unnumberedWire: 'وائر (نمبر باقی)',
    notYetSynced: 'ابھی بھیجا نہیں گیا',
    pendingSync: '{{count}} کیسز ابھی اسی آلے میں ہیں، سرور تک نہیں پہنچے۔',
    offlineShowingCached: 'آپ آف لائن ہیں۔ جو اس آلے میں محفوظ ہے وہی دکھایا جا رہا ہے۔',
    chakShort: 'چک {{chak}}',

    framableHeading: 'تصدیق شدہ وائر جن کا پرچہ تاوان باقی ہے',
    noFramableWires: 'کوئی تصدیق شدہ وائر زیرِ التوا نہیں۔ کیس ہمیشہ تصدیق شدہ وائر سے بنتا ہے۔',
    frameFromWire: 'کیس بنائیں',

    frameHeading: 'پرچہ تاوان بنائیں',
    frameCase: 'کیس بنائیں',

    onBehalfLegend: 'کس کی طرف سے؟',
    onBehalfHelp:
      'آپ بطور ڈیٹا آپریٹر اندراج کر رہے ہیں۔ کیس کی ذمہ داری پٹواری کی رہے گی، اندراج کی ذمہ داری آپ کی۔ دونوں درج ہوں گے۔',
    patwariUid: 'پٹواری اکاؤنٹ آئی ڈی',
    patwariUidHelp: 'وہ پٹواری جس نے یہ پرچہ تاوان بنایا۔',

    accusedLegend: 'ملزم',
    accusedHelp:
      'صرف اندراج ہے، تصدیق نہیں۔ نادرا کی عوامی تصدیق دستیاب نہیں، اس لیے شناختی کارڈ کا صرف فارمیٹ دیکھا جاتا ہے — یہاں کچھ بھی شناخت کی تصدیق نہیں کرتا۔',
    name: 'نام',
    nameUr: 'نام (اردو)',
    fatherName: 'والد کا نام',
    fatherNameUr: 'والد کا نام (اردو)',
    urduHelp: 'پرچہ تاوان اور سماعت کے نوٹس پر یہی چھپے گا۔',
    cnic: 'شناختی کارڈ',
    cnicHelp: 'صرف فارمیٹ دیکھا جاتا ہے۔ اس سے شخص کی تصدیق نہیں ہوتی۔',
    phone: 'فون (اختیاری)',

    landLegend: 'اراضی',
    landHelp: 'جیسا کہ مالیہ ریکارڈ میں درج ہے۔',
    chakNo: 'چک نمبر',
    mauza: 'موضع',
    khasraNumbers: 'خسرہ نمبر',
    khasraHelp: 'کاما یا خالی جگہ سے الگ کریں۔ اب تک {{count}} پہچانے گئے۔',

    cropLegend: 'فصل اور بلااجازت سیراب شدہ رقبہ',
    cropHelp: 'رقبہ ایکڑ، کنال اور مرلہ میں درج کریں۔ ایک ایکڑ = ۸ کنال، ایک کنال = ۲۰ مرلہ۔',
    crop: 'فصل',
    acres: 'ایکڑ',
    kanals: 'کنال',
    marlas: 'مرلہ',
    totalAcres: 'کل: {{acres}} ایکڑ',
    areaAcres: 'رقبہ (ایکڑ)',

    penaltyIndicative: 'اندازاً جرمانہ',
    penaltyUnavailable: 'جرمانے کا کوئی عدد نہیں نکالا جا سکتا',
    penaltyStillFrame:
      'آپ پھر بھی کیس بنا سکتے ہیں۔ پرچہ حقائق درج کرتا ہے؛ جرمانہ فیصلہ کرنے والا افسر تب مقرر کرے گا جب تصدیق شدہ نرخ نوٹیفکیشن موجود ہو۔',
    penaltyNotPriced: 'اس کیس پر جرمانے کا عدد نہیں — کوئی تصدیق شدہ نرخ نوٹیفکیشن نافذ نہیں تھا۔',
    penaltyNoSchedules: 'ابھی تک کوئی نرخ نوٹیفکیشن نظام میں شامل نہیں کیا گیا۔',
    penaltyAwaitingArea: 'اندازاً جرمانہ دیکھنے کے لیے رقبہ درج کریں۔',
    penaltySchedule:
      '{{version}} (نوٹیفکیشن {{notification}}) کے تحت، اُس نرخ پر جو چوری پکڑے جانے کے وقت نافذ تھا۔',
    ratePerAcre: 'فی ایکڑ نرخ',
    multiplier: 'ضرب',
    computedAmount: 'حساب شدہ',

    stanceLegend: 'ملزم کا بیان (اختیاری)',
    stanceHelp: 'کیس سامنے رکھنے پر ملزم نے کیا کہا، اگر کچھ کہا۔',

    framedByLine: 'پرچہ: {{patwari}} · اندراج: {{operator}}',
    availableActions: 'اب آپ کیا کر سکتے ہیں',
    reasonRequired: 'وجہ (آڈٹ لاگ میں مستقل درج ہو گی)',
    cannotDecideOwn:
      'یہ کیس اُس وائر سے بنا ہے جو آپ نے بھیجا تھا، اس لیے فیصلہ کوئی دوسرا افسر کرے گا۔',

    decisionLegend: 'فیصلہ درج کریں',
    outcome: 'نتیجہ',
    fineAmount: 'جرمانہ (روپے)',
    decisionRemarks: 'فیصلے کی وجوہات',
    remarksHelp:
      'اپیل کی عدالت یہی پڑھے گی۔ صرف "جرمانہ عائد" لکھنا ایسی بات نہیں جس کا کوئی جائزہ لے سکے۔',
    recordDecision: 'فیصلہ درج کریں',

    recordFir: 'ایف آئی آر درج کریں',
    firLegend: 'ایف آئی آر کی تفصیل',
    firHelp:
      'یہ صرف حوالہ ہے۔ ایف آئی آر کی کوئی نقل محفوظ نہیں ہوتی — کاغذ کہاں فائل ہے، یہ کیس نوٹس میں لکھ دیں۔',
    firRegistered: 'ایف آئی آر درج',
    firNumber: 'ایف آئی آر نمبر',
    policeStation: 'تھانہ',
    firDate: 'ایف آئی آر کی تاریخ',
    firSections: 'دفعات',
    accusedCount: 'نامزد افراد',
    arrestedCount: 'گرفتار افراد',
    peraInvolved: 'پیرا شامل',

    error: {
      seasonRequired: 'منتخب کریں کہ کون سا موسم شمار ہو رہا ہے۔ ریٹ اسی پر منحصر ہے۔',
      invalid: 'براہِ کرم نشان زدہ خانہ دیکھیں۔',
      wireRequired: 'کیس تصدیق شدہ وائر سے ہی بننا چاہیے۔',
      patwariRequired: 'درج کریں کہ یہ پرچہ کس پٹواری نے بنایا۔',
      nameRequired: 'ملزم کا نام درج کریں۔',
      fatherNameRequired: 'والد کا نام درج کریں۔',
      cnicInvalid: 'درست ۱۳ ہندسوں کا شناختی کارڈ نمبر درج کریں۔',
      phoneInvalid: 'یہ پاکستانی موبائل نمبر نہیں لگتا۔',
      chakRequired: 'چک نمبر درج کریں۔',
      mauzaRequired: 'موضع درج کریں۔',
      khasraRequired: 'کم از کم ایک خسرہ نمبر درج کریں۔',
      khasraInvalid: 'کوئی ایک خسرہ نمبر پہچانی جانے والی صورت میں نہیں۔',
      cropRequired: 'فصل منتخب کریں۔',
      areaRequired: 'بلااجازت سیراب شدہ رقبہ درج کریں۔',
      areaTooLarge:
        'یہ رقبہ ایک کیس کے لیے معمول سے زیادہ ہے — دیکھ لیں کہ ایکڑ اور کنال درست خانوں میں ہیں۔',
      outcomeRequired: 'نتیجہ منتخب کریں۔',
      fineRequired: 'جرمانے کی رقم درج کریں۔',
      fineOnNonFineOutcome: 'اس نتیجے میں جرمانہ عائد نہیں ہوتا، اس لیے کوئی رقم درج نہیں ہو سکتی۔',
      remarksTooShort: 'ایسی وجوہات لکھیں جن کا اپیل کی عدالت جائزہ لے سکے۔',
      firNumberRequired: 'ایف آئی آر نمبر درج کریں۔',
      policeStationRequired: 'تھانہ درج کریں۔',
      firDateRequired: 'ایف آئی آر کی تاریخ درج کریں۔',
      firSectionsRequired: 'لاگو دفعات درج کریں۔',
      arrestedExceedsAccused: 'گرفتار افراد نامزد افراد سے زیادہ ہیں — دونوں اعداد دیکھ لیں۔',
      saveFailed: 'کیس محفوظ نہیں ہو سکا۔ دوبارہ کوشش کریں۔',
    },
  },

  transition: {
    notAllowedFromState: 'موجودہ حیثیت میں یہ کارروائی دستیاب نہیں۔',
    roleNotPermitted: 'آپ کا عہدہ یہ کارروائی نہیں کر سکتا۔',
    reasonRequired: 'اس کارروائی کے لیے تحریری وجہ درج کرنا ضروری ہے۔',
    selfActionForbidden: 'جو ریکارڈ آپ نے خود بنایا ہو، اُس پر آپ یہ کارروائی نہیں کر سکتے۔',
  },

  rbac: {
    capabilityNotHeld: 'آپ کے عہدے میں یہ اختیار شامل نہیں۔',
    outsideJurisdiction: 'یہ ریکارڈ آپ کے دائرہ اختیار سے باہر ہے۔',
    selfActionForbidden: 'جو ریکارڈ آپ نے خود بنایا ہو، اُس پر آپ کارروائی نہیں کر سکتے۔',
    makerCheckerViolation: 'تصدیق وہی شخص نہیں کر سکتا جس نے اندراج کیا ہو۔',
  },

  penalty: {
    noScheduleInForce: 'جس تاریخ کو یہ چوری پکڑی گئی، اُس وقت کوئی نرخ نوٹیفکیشن نافذ نہیں تھا۔',
    scheduleNotVerified:
      'نرخ نوٹیفکیشن کی ایڈمنسٹریٹر سے تصدیق نہیں ہوئی، اس لیے کسی اصل کیس پر جرمانہ لاگو نہیں کیا جا سکتا۔',
    noRateForCrop: 'نرخ نوٹیفکیشن میں اس فصل کا نرخ درج نہیں۔',
    areaRequired: 'رقبہ صفر سے زیادہ ہونا ضروری ہے۔',
    noBandForOffenceCount: 'اتنی سابقہ خلاف ورزیوں کے لیے کوئی جرمانہ درجہ مقرر نہیں۔',
  },

  observation: {
    kindRequired: 'منتخب کریں کہ یہ کس قسم کا مشاہدہ ہے۔',
    narrativeTooShort:
      'جو آپ نے دیکھا اُسے اتنی تفصیل سے لکھیں کہ سماعت میں اُس پر کارروائی ہو سکے۔ چونکہ کوئی تصویر محفوظ نہیں کی جاتی، آپ کی تحریر ہی شہادت ہے۔',
    attributionRequired: 'مشاہدے کے ساتھ لکھنے والے کا نام درج ہونا ضروری ہے۔',
  },

  documentRef: {
    typeRequired: 'دستاویز کی قسم منتخب کریں۔',
    referenceNumberRequired: 'دستاویز یا حوالہ نمبر درج کریں۔',
    issuedByRequired: 'جاری کرنے والا دفتر، تھانہ، بینک برانچ یا عدالت درج کریں۔',
    issuedDateRequired: 'دستاویز کے اجرا کی تاریخ درج کریں۔',
  },

  reconciliation: {
    CLAIMED: 'دعویٰ — ابھی تصدیق نہیں ہوئی',
    OFFICER_VERIFIED: 'دوسرے افسر نے تصدیق کی',
    TREASURY_RECONCILED: 'خزانہ سے تصدیق شدہ',
    RECONCILIATION_FAILED: 'خزانہ میں اس رقم کا اندراج نہیں',
  },

  voucher: {
    action: {
      OFFICER_VERIFIED: 'رسید کے مطابق تصدیق کریں',
      TREASURY_RECONCILED: 'خزانہ سکرول سے تصدیق کریں',
      RECONCILIATION_FAILED: 'تصدیق کی ناکامی درج کریں',
    },
    error: {
      challanNumberInvalid: 'درست چالان 32-A نمبر درج کریں۔',
      challanNumberDuplicate:
        'یہ چالان نمبر پہلے ہی اس مقدمہ پر درج ہے۔ ایک رسید دو بار جمع نہیں ہو سکتی۔',
      bankRequired: 'بینک درج کریں۔',
      branchCodeRequired: 'برانچ کوڈ درج کریں۔',
      depositDateRequired: 'جمع کرانے کی تاریخ درج کریں۔',
      depositDateInFuture: 'آئندہ تاریخ کی رسید ابھی وجود میں نہیں آئی۔',
      amountRequired: 'جمع شدہ رقم درج کریں۔',
      amountNotWholeRupees: 'پورے روپے درج کریں۔ اعشاریہ رقم کبھی خزانہ سے مطابقت نہیں کرے گی۔',
      exceedsDemand:
        'یہ مطالبہ سے زیادہ وصولی ہو گی۔ زائد رقم بغیر حکم کے وصول شدہ سرکاری رقم ہے — پہلے اعداد و شمار درست کریں۔',
      transitionNotAllowed: 'اس مرحلے پر یہ قدم دستیاب نہیں۔',
      roleNotPermitted: 'آپ کا عہدہ یہ قدم نہیں اٹھا سکتا۔',
      rankTooJunior: 'خزانہ سے تصدیق ایگزیکٹو انجینئر یا بالا افسر درج کرتا ہے۔',
      reasonRequired: 'تحریری وجہ درکار ہے۔',
      sameAsEnteringOfficer:
        'جس افسر نے واؤچر درج کیا وہ خود اس کی تصدیق نہیں کر سکتا۔ دوسرا شخص ضروری ہے۔',
      sameAsVerifyingOfficer:
        'جس افسر نے تصدیق کی وہ خزانہ سے مطابقت بھی نہیں کر سکتا۔ یہ تیسرے فرد کا کام ہے۔',
    },
  },

  recovery: {
    count: '{{count}} مقدمات پر مطالبہ',
    noneYet: 'ابھی کوئی مقدمہ وصولی کے مرحلے پر نہیں پہنچا۔',
    vouchers: 'اس مقدمہ کے چالان',
    noVouchers: 'اس مقدمہ پر ابھی کچھ جمع نہیں ہوا۔',
    enterChallan: 'چالان 32-A درج کریں',
    enterChallanNote:
      'اس نظام میں رسید کی تصویر کہیں محفوظ نہیں ہوتی۔ چالان نمبر، بینک، برانچ اور تاریخ ہی پورا بیان ہیں، اور دوسرے افسر کی تصدیق لازم ہے۔',
    normalisedAs: 'اس نام سے درج ہو گا',
    recordClaim: 'بطور دعویٰ درج کریں',
    entryBlocked: 'اس وقت اس مقدمہ پر کوئی چالان درج نہیں ہو سکتا۔',
    enteredBy: 'درج کنندہ',
    verifiedBy: 'تصدیق کنندہ',
    reconciledBy: 'خزانہ تصدیق کنندہ',
    treasuryScroll: 'خزانہ سکرول',
    notYet: 'ابھی نہیں',
    failureReason: 'وجہ',
    reasonLabel: 'تحریری وجہ',
    treasuryScrollLabel: 'خزانہ سکرول / کریڈٹ حوالہ',
    blocked: {
      stay: 'وصولی رکی ہوئی ہے: اس مقدمہ پر عدالتی حکم امتناع نافذ ہے۔',
      unknown: 'اس وقت اس مقدمہ پر وصولی نہیں ہو سکتی۔',
    },
    figure: {
      demand: 'مطالبہ',
      originalDemand: 'اصل عائد شدہ',
      revisedOnAppeal: 'اپیل سطح {{level}} پر نظرثانی شدہ',
      recovered: 'وصول شدہ',
      outstanding: 'واجب الادا',
      proven: 'خزانہ سے تصدیق شدہ',
      provenNote: 'وہ رقم جو سرکاری کھاتے میں پہنچنا ثابت ہے۔',
      unproven: 'ابھی غیر تصدیق شدہ',
      unprovenNote: 'دو افسر رسید کی تصدیق کرتے ہیں؛ خزانہ نے ابھی کریڈٹ کی تصدیق نہیں کی۔',
      failed: 'خزانہ نے تردید کی',
      failedNote: 'خزانہ میں اس رقم کا کوئی اندراج نہیں۔ کبھی وصولی شمار نہیں ہو گی۔',
      stayedDays: 'حکم امتناع کے دن',
      stayedDaysNote: 'واجبات کی عمر سے منہا — ان دنوں میں محکمہ کارروائی نہیں کر سکتا تھا۔',
    },
    field: {
      challan: 'چالان 32-A نمبر',
      bank: 'بینک',
      branch: 'برانچ کوڈ',
      amount: 'جمع شدہ رقم (روپے)',
      depositDate: 'جمع کرانے کی تاریخ',
      fileLocation: 'کاغذی رسید کہاں محفوظ ہے',
      court: 'عدالت',
      courtCaseNumber: 'عدالتی مقدمہ نمبر',
      grantedAt: 'حکم امتناع کی تاریخ',
    },
    stays: 'عدالتی احکامِ امتناع',
    noStays: 'اس مقدمہ پر کوئی حکم امتناع درج نہیں۔',
    recordStay: 'حکم امتناع درج کریں',
    recordStayBlocked:
      'ایک حکم امتناع پہلے سے نافذ ہے۔ دوسرا درج کرنے سے پہلے اُس کا اختتام درج کریں تاکہ تاریخ واضح رہے۔',
    stayRunning: 'نافذ',
    stayVacated: 'ختم شدہ',
    stayedDaysSoFar: 'اب تک {{count}} دن',
    vacatedReason: 'ختم کرنے کی بنیاد',
    vacateReasonLabel: 'ختم کرنے کی بنیاد (وصولی دوبارہ شروع ہو گی)',
    vacateStay: 'اختتام درج کریں',
  },

  appealOutcome: {
    UPHELD: 'برقرار',
    MODIFIED: 'ترمیم شدہ',
    SET_ASIDE: 'کالعدم',
    REMANDED: 'دوبارہ سماعت کے لیے واپس',
  },

  appeal: {
    noneYet: 'اپیل کے لیے کوئی فیصلہ شدہ مقدمہ دستیاب نہیں۔',
    noAppealsOnCase: 'اس مقدمہ پر کوئی اپیل دائر نہیں ہوئی۔',
    orderUnderChallenge: 'زیرِ اپیل حکم',
    originalOrder: '{{outcome}} — {{amount}} روپے',
    supersededBy: 'اپیل سطح {{level}} پر تبدیل: اب {{amount}} روپے قابلِ وصول ہیں۔',
    noDecisionYet: 'اس مقدمہ کا فیصلہ ابھی نہیں ہوا۔',
    level: 'اپیل — سطح {{level}}',
    pending: 'زیرِ التوا',
    fileAppeal: 'سطح {{level}} پر اپیل درج کریں',
    appellantName: 'اپیل کنندہ',
    filedDate: 'دائر کرنے کی تاریخ',
    groundsLabel: 'اپیل کی بنیادیں',
    groundsHint:
      'اپیلٹ اتھارٹی "ناانصافی" کا جائزہ نہیں لے سکتی — اور نہ ہی ایک سال بعد پڑھنے والا اگلا افسر۔',
    suspensionRefusedLabel: 'اپیلٹ اتھارٹی نے وصولی روکنے سے انکار کیا',
    suspensionRefused: 'وصولی جاری ہے: اپیلٹ اتھارٹی نے التوا سے انکار کیا۔',
    recordFiling: 'اندراج درج کریں',
    outcomeLabel: 'نتیجہ',
    revisedAmount: 'نظرثانی شدہ جرمانہ (روپے)',
    orderNumber: 'اپیلٹ حکم نمبر',
    issuedBy: 'جاری کنندہ',
    issuedDate: 'حکم کی تاریخ',
    fileLocation: 'حکم کہاں محفوظ ہے',
    recordDecision: 'اپیلٹ حکم درج کریں',
    recusedFromDeciding:
      'آپ اس اپیل کا فیصلہ نہیں کر سکتے: یہ آپ ہی کے حکم کو چیلنج کرتی ہے، یا اس کا اندراج آپ نے کیا۔',
    limitationUnknown:
      'یہ اپیل میعاد کے اندر ہے یا نہیں، طے نہیں ہو سکتا: محکمہ نے ابھی اپیل کا درجہ بندی نظام یا میعاد مطلع نہیں کی۔',
    filedWithin: 'حکم کے {{days}} دن بعد دائر ہوئی۔',
    limitation: {
      ladderNotVerified:
        'اپیل کا درجہ بندی نظام محکمہ سے تصدیق شدہ نہیں، اس لیے کوئی اپیل میعاد سے باہر قرار نہیں دی جا سکتی۔',
      levelNotInLadder: 'یہ اپیل سطح مطلع شدہ نظام میں شامل نہیں۔',
    },
    demand: {
      setAside: 'حکم اپیل میں کالعدم ہوا۔ وصولی کے لیے کوئی مطالبہ باقی نہیں۔',
      remanded: 'مقدمہ دوبارہ سماعت کے لیے واپس بھیجا گیا۔ وصولی کے لیے کوئی نافذ حکم نہیں۔',
      suspendedPendingAppeal: 'اپیل زیرِ التوا رہنے تک وصولی معطل ہے۔',
    },
    error: {
      levelRequired: 'اپیل کی سطح درج کریں۔',
      appellantRequired: 'اپیل کنندہ کا نام درج کریں۔',
      filedDateRequired: 'اپیل دائر کرنے کی تاریخ درج کریں۔',
      appealedOrderDateRequired: 'زیرِ اپیل حکم کی تاریخ موجود نہیں۔',
      filedBeforeOrder: 'اپیل اُس حکم سے پہلے کی تاریخ نہیں رکھ سکتی جسے وہ چیلنج کرتی ہے۔',
      groundsTooShort: 'بنیادیں اتنی تفصیل سے لکھیں کہ اپیلٹ اتھارٹی جائزہ لے سکے۔',
      outcomeRequired: 'نتیجہ منتخب کریں۔',
      decidedDateRequired: 'اپیلٹ حکم کی تاریخ درج کریں۔',
      revisedAmountRequired: 'ترمیم شدہ نتیجہ کے لیے نظرثانی شدہ جرمانہ درکار ہے۔',
      revisedAmountNotWholeRupees: 'پورے روپے درج کریں۔',
      revisedAmountOnNonModified:
        'صرف ترمیم شدہ نتیجہ نظرثانی شدہ رقم رکھ سکتا ہے۔ کہیں اور یہ بغیر حکم کے مطالبہ ہو گا۔',
      orderReferenceRequired: 'اپیلٹ حکم کی شناخت درج کریں: نمبر، جاری کنندہ اور تاریخ۔',
    },
  },

  stay: {
    error: {
      courtRequired: 'عدالت درج کریں۔',
      caseNumberRequired: 'عدالتی مقدمہ نمبر درج کریں۔',
      grantedAtRequired: 'حکم امتناع کی تاریخ درج کریں۔',
      alreadyStayed:
        'اس مقدمہ پر پہلے سے حکم امتناع نافذ ہے۔ پہلے اُس کا اختتام درج کریں تاکہ تاریخ واضح رہے۔',
      alreadyVacated: 'یہ حکم امتناع پہلے ہی ختم ہو چکا۔ نیا حکم ایک الگ واقعہ ہے۔',
      vacatedBeforeGranted: 'حکم امتناع اپنے اجرا سے پہلے ختم نہیں ہو سکتا۔',
      reasonRequired: 'اختتام سے کسی شخص کے خلاف وصولی دوبارہ شروع ہوتی ہے۔ بنیاد بیان کریں۔',
    },
  },

  kpi: {
    totalCases: 'کل مقدمات',
    pendingCases: 'زیرِ التوا',
    pendingNote: 'محکمہ کے پاس زیرِ کار۔ عدالتی حکم امتناع والے مقدمات شامل نہیں۔',
    stayedCases: 'عدالتی حکم امتناع',
    stayedNote: 'الگ رپورٹ ہوتے ہیں، انتظامی تاخیر شمار نہیں ہوتے۔',
    decidedCases: 'فیصلہ شدہ',
    firRegistered: 'ایف آئی آر درج',
    finesImposed: 'عائد جرمانہ',
    finesRecovered: 'وصول شدہ',
    arrears: 'واجبات',
    arrearsNote: 'عدالتی حکم امتناع والے مقدمات شامل نہیں۔',
    unprovenRecovery: 'ابھی غیر تصدیق شدہ',
    unprovenNote: 'دو افسروں نے تصدیق کی؛ خزانہ نے کریڈٹ کی تصدیق نہیں کی۔',
    failedReconciliation: 'خزانہ نے تردید کی',
    failedNote: 'خزانہ میں ان رقوم کا اندراج نہیں۔ معاملہ اوپر بھیجا گیا۔',
  },

  ageing: {
    current: '30 دن سے کم',
    days_30: '30–59 دن',
    days_60: '60–89 دن',
    days_90_plus: '90 دن یا زائد',
  },

  zone: {
    bahawalpur: 'بہاولپور',
    sargodha: 'سرگودھا',
    sahiwal: 'ساہیوال',
    faisalabad: 'فیصل آباد',
    multan: 'ملتان',
    lahore: 'لاہور',
    dg_khan: 'ڈی جی خان',
  },

  dashboard: {
    noRollupYet:
      'آپ کے دائرہ اختیار کے اعداد و شمار ابھی مرتب نہیں ہوئے۔ رات کا عمل انہیں بناتا ہے؛ اس کا مطلب یہ نہیں کہ کوئی مقدمہ موجود نہیں۔',
    staleWarning: 'یہ اعداد و شمار ایک دن سے زیادہ پرانے ہیں۔ رات کا عمل شاید نہیں چلا۔',
    recomputedAt: 'اعداد و شمار بمطابق {{at}}',
    reads: '{{count}} خلاصہ دستاویزات سے، مقدمات کے ریکارڈ سے نہیں',
    caseTrend: 'چھ ماہ کے مقدمات',
    moneyTrend: 'عائد بمقابلہ وصول شدہ (روپے)',
    outcomes: 'فیصلے اور نتائج',
    topCanals: 'سب سے زیادہ مقدمات والی نہریں',
    ageing: 'واجبات بلحاظ عمر',
    theftTypes: 'پانی کس طریقے سے لیا گیا',
    pendingByZone: 'زون کے لحاظ سے زیرِ التوا',
    noZoneData: 'زون کے اعداد و شمار دستیاب نہیں۔',
    recentActivity: 'حالیہ سرگرمی',
    activityRestricted:
      'آڈٹ لاگ صرف سیکرٹری اور سسٹم ایڈمنسٹریٹر کے لیے کھلا ہے۔ یہ جان بوجھ کر ہے: کس نے کیا کیا، اس کا ناقابلِ ترمیم ریکارڈ نگرانی کا آلہ ہے، انتظامی سہولت نہیں۔',
    noActivity: 'ابھی کچھ درج نہیں ہوا۔',
    exportCsv: 'CSV برآمد کریں',
    backToProvince: 'پورے صوبے پر واپس',
    csv: {
      scope: 'دائرہ',
      asAt: 'اعداد و شمار بمطابق',
      figure: 'عنوان',
      value: 'قدر (جہاں لاگو ہو، روپے)',
      monthly: 'ماہ بہ ماہ',
      month: 'ماہ',
    },
    zoneComparison: 'زون بلحاظ زون',
    zone: 'زون',
    zoneNoData: 'ابھی مرتب نہیں',
    noDecisionsYet: 'ابھی کسی مقدمہ کا فیصلہ نہیں ہوا۔',
    noCanalData: 'ابھی کسی مقدمہ پر نہر درج نہیں ہوئی۔',
    noArrears: 'کوئی رقم واجب الادا نہیں۔',
    noTheftData: 'ابھی چوری کی قسم درج نہیں ہوئی۔',
    showTable: 'ان اعداد و شمار کو جدول میں دیکھیں',
    hideTable: 'جدول چھپائیں',
    tableCaption: 'اس صفحے کے تمام اعداد و شمار، تحریری صورت میں۔ {{count}} خلاصہ دستاویزات سے۔',
    series: {
      framed: 'درج شدہ مقدمات',
      decided: 'فیصلہ شدہ',
      imposed: 'عائد',
      recovered: 'وصول شدہ',
    },
  },

  insights: {
    mine: 'آپ کے فیصلے',
    mineIntro: 'اس حلقے میں آپ کے طے شدہ تمام مقدمات، اور آپ کے احکامات کے عوض اب تک وصول شدہ رقم۔',
    comparison: 'افسر برائے افسر',
    howToRead:
      'سطر کو عرضاً پڑھیں، کسی ایک کالم کو نہیں۔ کم شرح ضرب بذات خود ثبوت نہیں — چھوٹے رقبے اور پہلی بار کے جرائم کا نتیجہ بھی یہی ہوتا ہے۔ وصولی وہ کالم ہے جس میں جھوٹ ممکن نہیں۔',
    officer: 'افسر',
    decided: 'طے شدہ مقدمات',
    cases: 'مقدمات',
    acres: 'ایکڑ',
    season: 'فصل',
    multiplier: 'شرح ضرب',
    multiplierNote: 'اوسط ×{{avg}}',
    imposed: 'عائد جرمانے',
    recovered: 'وصول شدہ',
    recoveredNote: 'آپ کے عائد کردہ کا {{rate}}',
    recoveryRate: 'وصولی فیصد',
    tooFew: '{{n}} سے کم مقدمات — موازنے کے لیے ناکافی',
    omitted: 'سب سے کم فیصلوں والے {{count}} افسر اس فہرست میں شامل نہیں۔',
    noFlags:
      '\u0627\u0633 \u062d\u0644\u0642\u06d2 \u0645\u06cc\u06ba \u06a9\u0648\u0626\u06cc \u0627\u0641\u0633\u0631 \u06a9\u0633\u06cc \u0628\u06be\u06cc \u0642\u0627\u0639\u062f\u06d2 \u067e\u0631 \u067e\u0648\u0631\u0627 \u0646\u06c1\u06cc\u06ba \u0627\u062a\u0631\u062a\u0627\u06d4',
    rulesToggle: '\u0646\u0634\u0627\u0646 \u06a9\u0628 \u0644\u06af\u062a\u0627 \u06c1\u06d2',
    rulesIntro:
      '\u0646\u0634\u0627\u0646 \u0627\u0641\u0633\u0631 \u0633\u06d2 \u067e\u0648\u0686\u06be\u0627 \u062c\u0627\u0646\u06d2 \u0648\u0627\u0644\u0627 \u0633\u0648\u0627\u0644 \u06c1\u06d2\u060c \u0627\u0646 \u06a9\u06d2 \u062e\u0644\u0627\u0641 \u0641\u06cc\u0635\u0644\u06c1 \u0646\u06c1\u06cc\u06ba\u06d4 \u06c1\u0631 \u0642\u0627\u0639\u062f\u06c1 \u0637\u06d2 \u0634\u062f\u06c1 \u0627\u0648\u0631 \u0638\u0627\u06c1\u0631 \u06c1\u06d2\u060c \u0627\u0648\u0631 \u06c1\u0631 \u0646\u0634\u0627\u0646 \u06a9\u06d2 \u0633\u0627\u062a\u06be \u0648\u06c1 \u0627\u0639\u062f\u0627\u062f \u0645\u0648\u062c\u0648\u062f \u06c1\u06cc\u06ba \u062c\u0646 \u0633\u06d2 \u0648\u06c1 \u0644\u06af\u0627\u06d4',
    rules: {
      minimumOnly:
        '{{n}} \u06cc\u0627 \u0632\u0627\u0626\u062f \u062c\u0631\u0645\u0627\u0646\u06d2\u060c \u0627\u0648\u0631 \u06c1\u0631 \u0627\u06cc\u06a9 \u00d71 \u067e\u0631\u06d4 \u0642\u0627\u0646\u0648\u0646 \u00d71 \u0633\u06d2 \u00d710 \u062a\u06a9 \u06a9\u06cc \u06af\u0646\u062c\u0627\u0626\u0634 \u062f\u06cc\u062a\u0627 \u06c1\u06d2\u06d4',
      belowPeers:
        '\u0627\u0648\u0633\u0637 \u0634\u0631\u062d \u0636\u0631\u0628 \u0627\u0633\u06cc \u062d\u0644\u0642\u06d2 \u06a9\u06d2 \u0627\u0641\u0633\u0631\u0627\u0646 \u06a9\u06d2 \u0648\u0633\u0637\u06cc\u06c1 \u06a9\u06d2 {{pct}}% \u06cc\u0627 \u0627\u0633 \u0633\u06d2 \u06a9\u0645\u06d4',
      notCollected:
        '{{days}} \u062f\u0646 \u0633\u06d2 \u067e\u0631\u0627\u0646\u06d2 \u0645\u0637\u0627\u0644\u0628\u0627\u062a \u0645\u06cc\u06ba \u0633\u06d2 {{pct}}% \u0633\u06d2 \u06a9\u0645 \u0648\u0635\u0648\u0644\u06cc\u06d4 \u062a\u0627\u0632\u06c1 \u0641\u06cc\u0635\u0644\u06d2 \u0627\u0648\u0631 \u0639\u062f\u0627\u0644\u062a\u06cc \u062d\u06a9\u0645 \u0627\u0645\u062a\u0646\u0627\u0639 \u06a9\u06d2 \u062f\u0646 \u0634\u0627\u0645\u0644 \u0646\u06c1\u06cc\u06ba\u06d4',
      strongRecovery:
        '\u062c\u0631\u0645\u0627\u0646\u06c1 \u062d\u0644\u0642\u06d2 \u06a9\u06d2 \u0648\u0633\u0637\u06cc\u06c1 \u06a9\u06d2 \u0628\u0631\u0627\u0628\u0631 \u06cc\u0627 \u0632\u06cc\u0627\u062f\u06c1\u060c \u0627\u0648\u0631 \u067e\u0631\u0627\u0646\u06d2 \u0645\u0637\u0627\u0644\u0628\u0627\u062a \u06a9\u06cc {{pct}}% \u06cc\u0627 \u0632\u06cc\u0627\u062f\u06c1 \u0648\u0635\u0648\u0644\u06cc\u06d4',
    },
    rulesNotPositional:
      '\u06a9\u0648\u0626\u06cc \u0642\u0627\u0639\u062f\u06c1 \u062f\u0631\u062c\u06c1 \u0628\u0646\u062f\u06cc \u067e\u0631 \u0645\u0628\u0646\u06cc \u0646\u06c1\u06cc\u06ba \u2014 \u0641\u06c1\u0631\u0633\u062a \u0645\u06cc\u06ba \u0622\u062e\u0631\u06cc \u06c1\u0648\u0646\u06d2 \u06a9\u0627 \u06a9\u0648\u0626\u06cc \u0646\u0634\u0627\u0646 \u0646\u06c1\u06cc\u06ba\u06d4 \u062c\u0633 \u062d\u0644\u0642\u06d2 \u0645\u06cc\u06ba \u0633\u0628 \u0627\u0641\u0633\u0631 \u0679\u06be\u06cc\u06a9 \u06a9\u0627\u0645 \u06a9\u0631 \u0631\u06c1\u06d2 \u06c1\u0648\u06ba \u0648\u06c1\u0627\u06ba \u06a9\u0648\u0626\u06cc \u0646\u0634\u0627\u0646 \u0646\u06c1\u06cc\u06ba \u0627\u0679\u06be\u062a\u0627\u060c \u0627\u0648\u0631 \u0635\u0631\u0641 \u0628\u06be\u0627\u0631\u06cc \u062c\u0631\u0645\u0627\u0646\u06d2 \u0644\u06af\u0627 \u06a9\u0631 \u0646\u0634\u0627\u0646 \u06c1\u0679\u0627\u0646\u0627 \u0645\u0645\u06a9\u0646 \u0646\u06c1\u06cc\u06ba\u06d4',
    flag: {
      MINIMUM_ONLY: '\u06c1\u0645\u06cc\u0634\u06c1 \u00d71',
      BELOW_PEERS:
        '\u0633\u0627\u062a\u06be\u06cc\u0648\u06ba \u0633\u06d2 \u06a9\u0627\u0641\u06cc \u06a9\u0645',
      NOT_COLLECTED:
        '\u0648\u0635\u0648\u0644\u06cc \u0646\u06c1 \u06c1\u0648\u0646\u06d2 \u06a9\u06d2 \u0628\u0631\u0627\u0628\u0631',
      STRONG_RECOVERY: '\u0645\u0636\u0628\u0648\u0637 \u0648\u0635\u0648\u0644\u06cc',
    },
    why: {
      MINIMUM_ONLY:
        '\u062a\u0645\u0627\u0645 {{finedCases}} \u062c\u0631\u0645\u0627\u0646\u06d2 \u06a9\u0645 \u062a\u0631\u06cc\u0646 \u0642\u0627\u0646\u0648\u0646\u06cc \u0634\u0631\u062d \u067e\u0631\u06d4 \u062f\u0633\u062a\u06cc\u0627\u0628 \u062d\u062f \u00d71 \u0633\u06d2 \u00d710 \u062a\u06be\u06cc\u06d4',
      BELOW_PEERS:
        '\u0627\u0648\u0633\u0637 \u00d7{{average}}\u060c \u062c\u0628\u06a9\u06c1 \u0627\u0633 \u062d\u0644\u0642\u06d2 \u06a9\u0627 \u0648\u0633\u0637\u06cc\u06c1 \u00d7{{peerMedian}} \u06c1\u06d2\u06d4',
      NOT_COLLECTED:
        '{{days}} \u062f\u0646 \u0633\u06d2 \u067e\u0631\u0627\u0646\u06d2 {{maturedCases}} \u0645\u0637\u0627\u0644\u0628\u0627\u062a \u067e\u0631 {{maturedImposed}} \u0631\u0648\u067e\u06d2 \u0639\u0627\u0626\u062f\u061b {{rate}}% \u0648\u0635\u0648\u0644\u06d4',
      STRONG_RECOVERY:
        '{{imposed}} \u0631\u0648\u067e\u06d2 \u0639\u0627\u0626\u062f\u060c \u0648\u0633\u0637\u06cc\u06c1 {{peerMedian}} \u0631\u0648\u067e\u06d2 \u06a9\u06d2 \u0645\u0642\u0627\u0628\u0644\u06d2 \u0645\u06cc\u06ba\u060c \u0627\u0648\u0631 \u067e\u0631\u0627\u0646\u06d2 \u0645\u0637\u0627\u0644\u0628\u0627\u062a \u06a9\u06cc {{rate}}% \u0648\u0635\u0648\u0644\u06cc\u06d4',
    },
    rank: {
      recovered: 'بلحاظ وصولی',
      imposed: 'بلحاظ جرمانہ',
      cases: 'بلحاظ تعداد',
      severity: 'بلحاظ شرح ضرب',
    },
  },

  handover: {
    title: 'حوالگی',
    intro:
      'وہ کام جو اس دائرۂ اختیار سے افسر کے تبادلے پر باقی رہ گیا۔ یہاں کچھ بھی خودکار طور پر منتقل نہیں ہوا — کون سنبھالے گا، یہ محکمانہ فیصلہ ہے۔',
    none: 'یہاں کسی تبادلے سے کوئی کام زیرِ التوا نہیں چھوٹا۔',
    left: '{{name}} کا یہاں سے تبادلہ ہو چکا ہے',
    until: 'یہ عہدہ {{date}} تک رہا',
    snapshotLag:
      'یہ فہرست تقرری ختم ہونے کے {{days}} دن بعد مرتب ہوئی، اس لیے یہ اُسی بعد والی تاریخ کی صورتِ حال بتاتی ہے',
    nothingOutstanding: 'اس افسر کے جانے کے وقت کوئی کام زیرِ التوا نہیں تھا۔',
    unreachableBanner:
      '{{count}} اندراج پر اب کوئی بھی کارروائی نہیں کر سکتا، ایڈمنسٹریٹر بھی نہیں۔ تفصیل نیچے ہے۔',
    state: {
      OPEN: 'ابھی کسی نے نہیں سنبھالا',
      ACKNOWLEDGED: 'سنبھال لیا گیا',
    },
    kind: {
      wire: 'چوری وائر',
      case: 'تاوان مقدمہ',
      voucher: 'چالان 32-A',
    },
    note: {
      draftWireUnreachable:
        'کبھی روانہ نہیں ہوا۔ صرف اس کا لکھنے والا ہی اسے روانہ کر سکتا تھا اور اس کی رسائی منتقل ہو چکی ہے — چنانچہ اب کوئی نہیں کر سکتا، اور مقررہ مدت گزرنے پر یہ ضائع کر دیا جائے گا۔ اگر چوری کی کارروائی جاری رکھنی ہے تو کسی حاضر سب انجینئر کو اس کا مواد دوبارہ درج کرنا ہوگا۔',
      wireAwaitingSdo: 'روانہ ہو چکا ہے اور ایس ڈی او کے پاس زیرِ غور ہے۔',
      wireAwaitingConversion: 'ایس ڈی او نے تصدیق کر دی؛ تاوان مقدمہ ابھی قائم نہیں ہوا۔',
      voucherFailed: 'خزانے میں ایسی کوئی رقم جمع نہیں ملی۔ کاغذی رسید تلاش کرنا ہوگی۔',
      voucherUnproven: 'درج ہو چکا ہے مگر خزانہ اسکرول سے تصدیق باقی ہے۔',
    },
    noteLabel: 'یہ کام اب کون سنبھال رہا ہے؟',
    noteHint:
      'افسران کے نام لکھیں اور یہ کہ ہر ایک کیا سنبھال رہا ہے۔ اس فیصلے کا یہی واحد ریکارڈ ہے — کچھ بھی خودکار منتقل نہیں ہوتا۔',
    acknowledge: 'حوالگی درج کریں',
    acknowledgedBy: '{{date}} کو سنبھالا گیا',
    readOnlyNote:
      'آپ کے سب ڈویژن کا زیرِ التوا کام، برائے اطلاع۔ کون سنبھالے گا یہ درج کرنا ایس ڈی او یا اس سے اوپر کا کام ہے۔',
    error: {
      notSupervisor: 'حوالگی درج کرنا ایس ڈی او یا اس سے اوپر کے افسر کا کام ہے۔',
      alreadyAcknowledged: 'یہ حوالگی پہلے ہی سنبھالی جا چکی ہے۔',
      noteTooShort: 'کام سنبھالنے والے افسران کے نام لکھیں اور یہ کہ ہر ایک کیا لے رہا ہے۔',
    },
  },

  directory: {
    intro:
      'کون سائن اِن کر سکتا ہے۔ پتہ درج کرنے سے اکاؤنٹ نہیں بنتا — وہ گوگل اُس وقت بناتا ہے جب افسر پہلی بار سائن اِن کرے — چنانچہ رسائی اُن کی پہلی کوشش کے تقریباً پندرہ منٹ بعد آتی ہے، فوراً نہیں۔',
    enrolTitle: 'افسر درج کریں',
    email: 'گوگل پتہ',
    fullName: 'نام',
    designation: 'عہدہ',
    role: 'کردار',
    enrol: 'ڈائریکٹری میں شامل کریں',
    willMatchAs: 'اس شکل میں ملایا جائے گا:',
    awaitingTitle: 'درج ہیں، ابھی سائن اِن نہیں کیا',
    awaitingNote:
      'یہ پتے ڈائریکٹری میں ہیں مگر ابھی کسی گوگل اکاؤنٹ سے نہیں ملے۔ افسر کے پہلے سائن اِن سے پہلے یہ معمول ہے — لیکن جو پتہ یہیں رکا رہے وہ عموماً ٹائپنگ کی غلطی ہوتی ہے، اور افسر کو کتنا ہی انتظار کرنے پر ”ابھی رسائی نہیں“ ہی نظر آئے گا۔',
    activeTitle: 'فعال',
    noneActive: 'ابھی کسی نے سائن اِن نہیں کیا۔',
    withdrawnTitle: 'واپس لی گئی',
    withdrawnNote:
      'حذف کرنے کے بجائے رکھی گئی ہے۔ حذف شدہ اندراج اور ”کبھی درج ہی نہیں تھا“ میں فرق نہیں رہتا، اور افسر اگلی بار سائن اِن کرتے ہی دوبارہ اندر آ جاتا۔',
    withdraw: 'رسائی واپس لیں',
    reinstate: 'بحال کریں',
    error: {
      emailRequired: 'افسر کا گوگل پتہ درج کریں۔',
      nameRequired: 'افسر کا نام درج کریں۔',
      roleRequired: 'کردار منتخب کریں۔',
      zoneRequired: 'اس افسر کا زون منتخب کریں۔',
      subDivisionRequiredForRole:
        'یہ کردار ایک سب ڈویژن میں کام کرتا ہے۔ اس کے بغیر اکاؤنٹ بن تو جائے گا مگر کچھ نہیں کر سکے گا۔',
    },
  },

  admin: {
    tab: {
      directory: 'ڈائریکٹری',
      schedules: 'جرمانہ شیڈول',
      officers: 'افسران اور تبادلے',
      import: 'بلک امپورٹ',
      audit: 'آڈٹ لاگ',
      retention: 'مدتِ حفاظت',
    },
    schedule: {
      additionalRatesNote:
        'یہ بھی نوٹیفکیشن میں ہے، درج کیا گیا ہے مگر کسی مطالبے پر لاگو نہیں — کیا اضافی آبیانہ ریٹ چوری کے جرمانے کا حصہ بنتا ہے، اور سالانہ رقم موسمی رقم کے ساتھ کیسے جڑتی ہے، یہ محکمانہ سوالات ہیں:',
      gardensRate: 'منظور شدہ باغات، فی ایکڑ سالانہ',
      liftRate: 'سرکاری لفٹ اریگیشن، فی ایکڑ سالانہ',
      enter: 'نوٹیفکیشن درج کریں',
      enterTitle: 'جرمانہ شیڈول درج کریں',
      enterIntro:
        'براہِ راست آبیانہ نوٹیفکیشن سے۔ جو یہاں درج ہوگا وہ اُس وقت تک کسی مقدمے کی قیمت نہیں لگا سکتا جب تک دوسرا ایڈمنسٹریٹر اسے کاغذ سے ملا کر تصدیق نہ کرے۔',
      version: 'ورژن (یہی ریکارڈ کی شناخت بنے گا)',
      notificationNumber: 'نوٹیفکیشن نمبر',
      notificationDate: 'نوٹیفکیشن کی تاریخ',
      effectiveFrom: 'کس تاریخ سے نافذ',
      kharifRate: 'خریف ریٹ، فی ایکڑ روپے',
      rabiRate: 'ربیع ریٹ، فی ایکڑ روپے',
      roundToNearest: 'رقم کو قریب ترین اِس عدد تک',
      fileLocation: 'کاغذ کہاں فائل ہے (اختیاری)',
      secondAdminNote:
        'یہ غیر تصدیق شدہ محفوظ ہوگا۔ کسی بھی مقدمے کی قیمت لگنے سے پہلے دوسرے ایڈمنسٹریٹر کو اسے نوٹیفکیشن سے ملانا ہوگا۔ آپ اپنا درج کردہ خود تصدیق نہیں کر سکتے۔',
      save: 'غیر تصدیق شدہ محفوظ کریں',
      none: 'کوئی جرمانہ شیڈول درج نہیں ہوا۔',
      noneVerified:
        'کوئی تصدیق شدہ شیڈول موجود نہیں، اس لیے نظام کسی مقدمہ کی قیمت لگانے سے انکار کرے گا۔ یہ انکار جان بوجھ کر ہے: جن اعداد کی کسی نوٹیفکیشن سے تصدیق نہیں ہوئی، اُن سے قانونی مطالبہ نہیں بنایا جائے گا۔ موجودہ نوٹیفکیشن درج کریں اور دوسرے ایڈمنسٹریٹر سے تصدیق کروائیں۔',
      placeholderWarning:
        'یہ بیج کا عارضی شیڈول ہے۔ اس کے نرخ ترقیاتی مقاصد کے لیے فرضی ہیں اور اس کی کبھی تصدیق نہیں ہو سکتی۔',
      verified: 'تصدیق شدہ',
      unverified: 'ابھی تصدیق نہیں ہوئی',
      open: 'تاحال نافذ',
      enteredBy: 'درج کنندہ',
      verifiedBy: 'تصدیق کنندہ',
      notYet: 'ابھی نہیں',
      confirmLabel: 'نوٹیفکیشن نمبر، سامنے رکھے کاغذ سے',
      confirmHint:
        'نقل نہ کریں، خود ٹائپ کریں۔ جانچ یہ ہے کہ کسی نے گزٹ پڑھا، نہ کہ کسی نے ہاں پر کلک کیا۔',
      verify: 'اس شیڈول کی تصدیق کریں',
    },
    officers: {
      none: 'ابھی کوئی افسر درج نہیں ہوا۔',
      pendingSync:
        '{{count}} افسران کی تعیناتی درج ہے مگر اُن کی رسائی ابھی منتقل نہیں ہوئی۔ کلیمز سویپ پندرہ منٹ کے اندر نافذ کرتا ہے — تب تک اُن کے پاس پرانی رسائی ہی ہے۔',
      accessPending: 'رسائی ابھی منتقل نہیں ہوئی',
      noPosting: 'کوئی تعیناتی نہیں — کچھ نہیں دیکھ سکتے',
      since: '{{date}} سے',
      sinceBeforeRecords: 'اس نظام میں تعیناتیوں کے اندراج سے پہلے سے',
      present: 'تاحال',
      history: 'سروس ریکارڈ',
      transfer: 'تبادلہ درج کریں',
      newRole: 'نئی تعیناتی کا عہدہ',
      zone: 'زون',
      circle: 'سرکل',
      division: 'ڈویژن',
      subDivision: 'سب ڈویژن',
      effectiveFrom: 'چارج سنبھالنے کی تاریخ',
      recordTransfer: 'تبادلہ درج کریں',
      accessNote:
        'یہ تعیناتی درج کرتا ہے۔ رسائی اگلے کلیمز سویپ پر منتقل ہو گی، پندرہ منٹ کے اندر — تب تک افسر کے پاس پرانی رسائی رہے گی، اور یہ اسکرین یہی بتائے گی۔',
      handoverNote:
        'اس افسر کے زیرِ کار وائر یا مقدمات رات کو اُس سب ڈویژن کی ”حوالگی“ اسکرین پر درج ہو جاتے ہیں جسے وہ چھوڑ رہے ہیں — یہاں نہیں، کیونکہ ایڈمنسٹریٹر مقدمات کا مواد نہیں پڑھتا۔ کچھ بھی خودکار منتقل نہیں ہوتا؛ کون سنبھالے گا یہ محکمانہ فیصلہ ہے جو وہیں درج ہوتا ہے۔',
    },
    import: {
      adminOnly: 'بلک امپورٹ ایڈمنسٹریٹر کا کام ہے۔',
      entity: 'کیا امپورٹ کرنا ہے',
      entityName: {
        officers: 'افسران',
        subdivisions: 'سب ڈویژن',
      },
      pastRuns: 'پچھلے امپورٹ',
      noRuns: 'ابھی کوئی امپورٹ نہیں چلا۔',
      runCounts: '{{create}} نئے، {{update}} تبدیل شدہ',
      rollback: 'یہ امپورٹ واپس کریں',
      rollbackConfirm: 'ہاں، واپس کریں',
      rollbackWarning:
        'اس سے {{count}} سطریں واپس ہوں گی: اِس امپورٹ کی بنائی ہوئی سطریں غیر فعال ہو جائیں گی، اور تبدیل شدہ سطریں پہلی حالت پر بحال ہوں گی۔ بنائی گئی سطریں حذف نہیں ہوتیں، کیونکہ بعد میں درج ہونے والا کوئی مقدمہ اُن کا حوالہ دے سکتا ہے۔',
      alreadyRolledBack: 'پہلے ہی واپس ہو چکا۔',
      downloadTemplate: 'ٹیمپلیٹ ڈاؤن لوڈ کریں',
      chooseFile: 'CSV فائل منتخب کریں',
      dryRunNote:
        'جب تک آپ فرق نہ دیکھ لیں، کچھ نہیں لکھا جاتا۔ فائل پڑھ کر موجودہ ریکارڈ سے موازنہ ہوتا ہے؛ نافذ کرنے کا فیصلہ آپ کا ہے۔',
      planning: 'فائل پڑھی جا رہی ہے اور موازنہ ہو رہا ہے...',
      planHeading: 'یہ امپورٹ کیا کرے گا',
      outcome: {
        create: 'نیا',
        update: 'تبدیلی',
        skip: 'بغیر تبدیلی',
        error: 'مسترد',
      },
      showChanges: 'دیکھیں کون سے خانے بدلیں گے',
      fileErrors: 'فائل صحیح طور پر پڑھی نہیں جا سکی ({{count}} مسئلے):',
      line: 'سطر {{line}}',
      someRowsRejected:
        '{{count}} سطریں مسترد ہوئیں۔ باقی پھر بھی نافذ ہو سکتی ہیں - اِن کو درست کر کے الگ امپورٹ کریں۔',
      downloadErrors: 'غلطیوں کی رپورٹ ڈاؤن لوڈ کریں',
      commit: '{{count}} سطریں نافذ کریں',
      committed: '{{count}} سطریں نافذ ہوئیں۔ رن آئی ڈی:',
    },
    audit: {
      note: 'حالیہ {{count}} اندراجات۔ لاگ صرف اضافے کے لیے ہے اور مستقل ہے - یہاں کا کوئی اندراج کوئی بھی، بشمول ایڈمنسٹریٹر، تبدیل یا حذف نہیں کر سکتا۔',
      when: 'کب',
      who: 'عہدہ',
      what: 'کارروائی',
      subject: 'موضوع',
    },
    retention: {
      intro:
        'اس نظام میں تقریباً کچھ بھی حذف نہیں ہوتا۔ مدتِ حفاظت جان بوجھ کر استثنا ہے، اور اس کی سمت الٹ ہے: اِن دو اقسام کے لیے ریکارڈ ہمیشہ رکھنا ہی نقصان ہے۔',
      falseReports: 'شہری اطلاعات جو معائنے کے بعد جھوٹی قرار پائیں',
      draftWires: 'وہ وائر جو مسودہ رہے اور کبھی بھیجے نہ گئے',
      permanent: 'مقدمات، آڈٹ لاگ اور جرمانہ شیڈول',
      days: '{{count}} دن',
      never: 'کبھی ختم نہیں ہوتے',
      signOffPending:
        'یہ مدتیں محکمہ کی ڈیٹا پروٹیکشن منظوری کی منتظر ہیں (PART K9)۔ فی الحال رات کا عمل اِنہی کے مطابق چلتا ہے۔',
    },
  },

  csv: {
    error: {
      empty: 'فائل خالی ہے۔',
      duplicateColumn: 'دو کالموں کا نام ایک ہی ہے۔',
      wrongColumnCount:
        'اس سطر کے کالم ہیڈر سے مختلف ہیں - عام طور پر بغیر کوٹیشن کے کاما کی وجہ سے۔',
      strayQuote: 'بغیر کوٹیشن والے خانے کے درمیان کوٹیشن کا نشان ہے۔',
      unterminatedQuote: 'ایک کوٹیشن کبھی بند نہیں ہوا، جو باقی فائل نگل جاتا ہے۔',
      textAfterQuote: 'بند ہونے والے کوٹیشن کے بعد مزید متن ہے۔',
    },
  },

  import: {
    error: {
      requiredFieldBlank: 'ایک لازمی خانہ خالی ہے۔',
      unknownRole: 'یہ عہدہ اس نظام میں موجود نہیں۔',
      subDivisionRequiredForRole:
        'یہ عہدہ سب ڈویژن پر کام کرتا ہے، اور اس کے بغیر یہ اکاؤنٹ کچھ نہیں کر سکے گا۔',
      phoneFormat: 'فون نمبر 03001234567 کی طرح ہونا چاہیے۔',
      idBlank: 'شناخت کا خانہ خالی ہے۔',
      duplicateIdInFile: 'اسی فائل کی ایک اور سطر یہی شناخت رکھتی ہے۔',
      missingColumns: 'فائل میں ایک یا زائد لازمی کالم موجود نہیں۔',
      fileMalformed: 'فائل صحیح پڑھی نہیں جا سکی، اس لیے کسی سطر پر بھروسا نہیں کیا جا سکتا۔',
      nothingToDo: 'اس فائل سے کچھ تبدیل نہیں ہو گا۔',
      fatal: 'یہ فائل امپورٹ نہیں ہو سکتی۔',
    },
  },

  schedule: {
    error: {
      versionInvalid: 'حروف، ہندسے، نقطے، ڈیش یا انڈر سکور استعمال کریں - کم از کم تین۔',
      versionReserved: 'یہ ورژن بیج کے عارضی شیڈول کے لیے مخصوص ہے۔',
      notificationNumberRequired: 'نوٹیفکیشن نمبر درج کریں۔',
      notificationDateRequired: 'نوٹیفکیشن کی تاریخ درج کریں۔',
      effectiveFromRequired: 'نرخ نافذ ہونے کی تاریخ درج کریں۔',
      effectiveToInvalid: 'یہ اختتامی تاریخ درست نہیں۔',
      effectiveToBeforeFrom: 'شیڈول کھلنے سے پہلے بند ہو جائے گا۔',
      effectiveBeforeNotification: 'نرخ اُس نوٹیفکیشن سے پہلے نافذ نہیں ہو سکتے جس نے انہیں بنایا۔',
      ratesRequired: 'کم از کم ایک نرخ درج کریں۔',
      rateNegative: 'نرخ منفی نہیں ہو سکتا۔',
      ratesAllZero: 'ہر نرخ صفر ہے۔ یہی عارضی شیڈول میں ہوتا ہے۔',
      roundingInvalid: 'راؤنڈنگ پورے روپے میں ہو، کم از کم ایک۔',
      alreadyVerified: 'اس شیڈول کی تصدیق پہلے ہو چکی ہے۔',
      sameAsAuthor:
        'یہ شیڈول آپ نے درج کیا، اس لیے آپ اس کی تصدیق نہیں کر سکتے۔ سارا مقصد یہی ہے کہ کوئی اور جانچے۔',
      confirmNotificationNumber: 'کاغذ سے نوٹیفکیشن نمبر ٹائپ کریں۔',
      notificationNumberMismatch:
        'یہ اس شیڈول کے نمبر سے میل نہیں کھاتا۔ دیکھیں کہ آپ کے سامنے درست نوٹیفکیشن ہے۔',
    },
  },

  assignment: {
    error: {
      roleRequired: 'نئی تعیناتی کے لیے عہدہ منتخب کریں۔',
      effectiveFromRequired: 'چارج سنبھالنے کی تاریخ درج کریں۔',
      zoneRequired: 'زون درج کریں۔',
      subDivisionRequiredForRole:
        'یہ عہدہ سب ڈویژن پر کام کرتا ہے۔ اس کے بغیر افسر کچھ بھی نہیں کر سکے گا۔',
      notAfterCurrentPosting:
        'نئی تعیناتی افسر کی ہر موجودہ تعیناتی کے بعد شروع ہونی چاہیے۔ سروس ریکارڈ ماضی میں نہیں لکھا جا سکتا۔',
      noChange: 'افسر پہلے ہی اسی تعیناتی پر ہے۔',
    },
  },
};
